import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TableSortLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, Stack, CircularProgress, Alert, Tooltip,
  Autocomplete, Select, MenuItem, Menu, ListItemIcon, ListItemText,
  FormControl, InputLabel,
  Divider, Collapse, List,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import WifiIcon from '@mui/icons-material/Wifi';
import RouterIcon from '@mui/icons-material/Router';
import GroupsIcon from '@mui/icons-material/Groups';
import MapIcon from '@mui/icons-material/Map';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DevicesIcon from '@mui/icons-material/Devices';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import SecurityIcon from '@mui/icons-material/Security';
import DnsIcon from '@mui/icons-material/Dns';
import SaveIcon from '@mui/icons-material/Save';
import FolderIcon from '@mui/icons-material/Folder';
import ClearIcon from '@mui/icons-material/Clear';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import {
  buildRrmPlanIndex,
  capUpperTreeIdsFromConfig,
  getRrmDevicePlanRole,
  has5GHzPublicWifi,
  isCapDevice,
  DEFAULT_CAP_TREE_ID,
  isJnet2CapDevice,
  radioProfileNeedsCapTreeSelect,
  ssidBandNeedsCapTreeSelect,
  showApiWarnings,
} from '../utils/publicWifiRrmPlanUi';
import { RADIO_BAND_OPTIONS, formatRadioProfileBand } from '../utils/radioProfileBand';
import { formatSsidProfileOptionLabel } from '../utils/ssidProfileLabel';

const ENCRYPTION_OPTIONS = ['psk', 'psk2', 'none'];
/** Backend default when band is omitted on POST /ssids (display only in SSID list). */
const DEFAULT_SSID_BAND = '2.4GHz,5GHz';
const CHANNEL_WIDTH_OPTIONS_24 = ['HT20', 'HT40'];
const CHANNEL_WIDTH_OPTIONS_5 = ['HT20', 'HT40', 'HT80', 'HT160'];

/** Payload to clear remote syslog on devices (matches per-device SyslogSection empty state). */
const SYSLOG_EMPTY_CONFIG = Object.freeze({ ip: '', port: '', proto: 'udp', log_remote: '' });

function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ mt: 2 }}>
      {value === index && children}
    </Box>
  );
}

function deviceAutocompleteLabel(device) {
  const serial = (device?.serial_number || '').trim();
  const name = (device?.name || '').trim();
  if (!serial) return name || '';
  if (!name || name.toLowerCase() === serial.toLowerCase()) return serial;
  return `${name} (${serial})`;
}

function deviceMatchesSearch(device, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  const serial = (device?.serial_number || '').toLowerCase();
  const name = (device?.name || '').toLowerCase();
  return serial.includes(q) || name.includes(q);
}

function filterDevicesBySerialOrName(options, state) {
  const raw = typeof state === 'string' ? state : state?.inputValue;
  const q = String(raw ?? '').trim().toLowerCase();
  if (!q) return options;
  return options.filter((d) => deviceMatchesSearch(d, q));
}

function segmentSerialsFromTree(tree) {
  if (!tree) return [];
  const list = [
    ...(tree.devices || []),
    ...(tree.caps || []),
  ].map((s) => String(s).toLowerCase()).filter(Boolean);
  return [...new Set(list)];
}

function groupNameFromSegment(tree) {
  const base = String(tree?.name || '').trim() || `segment-${tree?.segment_id ?? 'unknown'}`;
  if (/\sgrp$/i.test(base)) return base;
  return `${base} grp`;
}

function buildSsidsByDevice(ssids) {
  const map = {};
  (ssids || []).forEach((s) => {
    (s.device_serials || []).forEach((serial) => {
      const key = String(serial).toLowerCase();
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
  });
  return map;
}

function DeviceDirectSsidIndicator({ serial, ssidsByDevice, compact = false }) {
  const list = ssidsByDevice?.[String(serial || '').toLowerCase()] || [];
  if (list.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: compact ? '0.68rem' : undefined }}>
        No direct SSID
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {list.map((s) => (
        <Tooltip key={s.id} title="SSID assigned directly to device (not via group)">
          <Chip
            icon={<WifiIcon sx={{ fontSize: compact ? '0.75rem !important' : undefined }} />}
            label={s.ssid || s.name}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: compact ? 20 : 22, fontSize: compact ? '0.68rem' : undefined }}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

// ─── SSID dialog ─────────────────────────────────────────────────────────────

function SSIDDialog({ open, onClose, onSave, initial, vlans = [], radiusProfiles = [], groups = [] }) {
  const isEdit = !!initial?.id;
  const assignedGroupCount = isEdit
    ? groups.filter(g => (g.ssid_ids || []).includes(initial.id)).length
    : 0;
  const isAssigned = assignedGroupCount > 0 || (initial?.device_serials?.length > 0);
  const blank = {
    ssid: '', encryption: 'psk2', key: '',
    vlan_profile_id: '', radius_profile_id: '',
    client_isolation: '', max_inactivity_seconds: '', max_num_sta: '',
    mcs_thr_min_not_low_mcs_idx: '', mcs_thr_low_mcs_ratio_thr_perc: '',
    mcs_thr_duration_sec: '', mcs_thr_deny_sec: '',
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      if (initial) {
        const mcs = initial.mcs_thr || {};
        setForm({
          ...blank, ...initial,
          vlan_profile_id: initial.vlan_profile_id || '',
          radius_profile_id: initial.radius_profile_id || '',
          client_isolation: initial.client_isolation || '',
          max_inactivity_seconds: initial.max_inactivity_seconds || '',
          max_num_sta: initial.max_num_sta || '',
          mcs_thr_min_not_low_mcs_idx: mcs.min_not_low_mcs_idx || '',
          mcs_thr_low_mcs_ratio_thr_perc: mcs.low_mcs_ratio_thr_perc || '',
          mcs_thr_duration_sec: mcs.duration_sec || '',
          mcs_thr_deny_sec: mcs.deny_sec || '',
        });
      } else {
        setForm(blank);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.ssid.trim()) { enqueueSnackbar('SSID is required', { variant: 'warning' }); return; }
    if (!form.encryption) { enqueueSnackbar('Encryption is required', { variant: 'warning' }); return; }
    if (form.encryption !== 'none' && !form.key.trim()) { enqueueSnackbar('Key is required', { variant: 'warning' }); return; }
    setSaving(true);
    try {
      const payload = {
        ssid: form.ssid.trim(),
        encryption: form.encryption,
        key: form.key,
        vlan_profile_id: form.vlan_profile_id || '',
        radius_profile_id: form.radius_profile_id || '',
      };
      const mcs = {
        min_not_low_mcs_idx: String(form.mcs_thr_min_not_low_mcs_idx ?? '').trim(),
        low_mcs_ratio_thr_perc: String(form.mcs_thr_low_mcs_ratio_thr_perc ?? '').trim(),
        duration_sec: String(form.mcs_thr_duration_sec ?? '').trim(),
        deny_sec: String(form.mcs_thr_deny_sec ?? '').trim(),
      };
      if (isEdit) {
        payload.client_isolation = String(form.client_isolation ?? '').trim();
        payload.max_inactivity_seconds = String(form.max_inactivity_seconds ?? '').trim();
        payload.max_num_sta = String(form.max_num_sta ?? '').trim();
        payload.mcs_thr = mcs;
      } else {
        if (form.client_isolation !== '') payload.client_isolation = String(form.client_isolation).trim();
        if (form.max_inactivity_seconds !== '') payload.max_inactivity_seconds = String(form.max_inactivity_seconds).trim();
        if (form.max_num_sta !== '') payload.max_num_sta = String(form.max_num_sta).trim();
        const mcsCreate = {};
        if (mcs.min_not_low_mcs_idx !== '') mcsCreate.min_not_low_mcs_idx = mcs.min_not_low_mcs_idx;
        if (mcs.low_mcs_ratio_thr_perc !== '') mcsCreate.low_mcs_ratio_thr_perc = mcs.low_mcs_ratio_thr_perc;
        if (mcs.duration_sec !== '') mcsCreate.duration_sec = mcs.duration_sec;
        if (mcs.deny_sec !== '') mcsCreate.deny_sec = mcs.deny_sec;
        if (Object.keys(mcsCreate).length > 0) payload.mcs_thr = mcsCreate;
      }
      await onSave(payload, isEdit ? initial.id : null);
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to save SSID profile', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit SSID Profile' : 'Create SSID Profile'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isEdit && isAssigned && (
            <Alert severity="warning">
              This SSID is assigned to {assignedGroupCount > 0 ? `${assignedGroupCount} group(s)` : 'devices'}.
              Saving changes may re-apply AP configuration on affected devices.
            </Alert>
          )}
          <TextField label="SSID *" value={form.ssid} onChange={set('ssid')} fullWidth size="small" />
          <FormControl fullWidth size="small">
            <InputLabel>Encryption *</InputLabel>
            <Select value={form.encryption} label="Encryption *" onChange={set('encryption')}>
              {ENCRYPTION_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label={form.encryption === 'none' ? 'Key (not required)' : 'Key *'}
            value={form.key}
            onChange={set('key')}
            fullWidth
            size="small"
            type="password"
          />
          <Divider><Typography variant="caption">Optional settings</Typography></Divider>
          <FormControl fullWidth size="small">
            <InputLabel>VLAN Profile</InputLabel>
            <Select value={form.vlan_profile_id} label="VLAN Profile" onChange={set('vlan_profile_id')}>
              <MenuItem value=""><em>None</em></MenuItem>
              {vlans.map(v => (
                <MenuItem key={v.id} value={v.id}>{v.name} (#{v.number})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>RADIUS Profile</InputLabel>
            <Select value={form.radius_profile_id} label="RADIUS Profile" onChange={set('radius_profile_id')}>
              <MenuItem value=""><em>None</em></MenuItem>
              {radiusProfiles.map(r => (
                <MenuItem key={r.id} value={r.id}>{r.name} ({r.auth_server}:{r.auth_port})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider><Typography variant="caption">Client Control</Typography></Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Client Isolation</InputLabel>
              <Select value={form.client_isolation} label="Client Isolation" onChange={set('client_isolation')}>
                <MenuItem value=""><em>Default</em></MenuItem>
                <MenuItem value="1">Enabled</MenuItem>
                <MenuItem value="0">Disabled</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Max Inactivity (sec)" value={form.max_inactivity_seconds} onChange={set('max_inactivity_seconds')} type="number" size="small" sx={{ flex: 1 }} />
            <TextField label="Max Stations" value={form.max_num_sta} onChange={set('max_num_sta')} type="number" size="small" sx={{ flex: 1 }} />
          </Box>
          <Divider>
            <Tooltip
              placement="top"
              title={
                <Box sx={{ fontSize: '0.78rem', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                  {'Automatically disconnects clients with poor wireless link quality.\n\n' +
                   'The device tracks how many packets are sent at low MCS rates\n' +
                   '(below the "Min Good MCS" index you set). If the percentage\n' +
                   'of low-rate packets stays above your chosen limit for the\n' +
                   'full observation window, the client is disconnected and\n' +
                   'blocked from reconnecting for the deny period.'}
                </Box>
              }
            >
              <Typography variant="caption" sx={{ cursor: 'help', borderBottom: '1px dashed', borderColor: 'text.secondary' }}>
                Weak-Client Auto-Kick
              </Typography>
            </Tooltip>
          </Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Min Good MCS" value={form.mcs_thr_min_not_low_mcs_idx} onChange={set('mcs_thr_min_not_low_mcs_idx')} type="number" size="small" sx={{ flex: 1 }}
              helperText="Rates below this index count as poor" />
            <TextField label="Max Low-Rate Traffic (%)" value={form.mcs_thr_low_mcs_ratio_thr_perc} onChange={set('mcs_thr_low_mcs_ratio_thr_perc')} type="number" size="small" sx={{ flex: 1 }}
              helperText="Disconnect when poor-rate traffic exceeds this %" />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Observation Window (sec)" value={form.mcs_thr_duration_sec} onChange={set('mcs_thr_duration_sec')} type="number" size="small" sx={{ flex: 1 }}
              helperText="How long poor quality must persist before kick" />
            <TextField label="Deny Period (sec)" value={form.mcs_thr_deny_sec} onChange={set('mcs_thr_deny_sec')} type="number" size="small" sx={{ flex: 1 }}
              helperText="Block the client from reconnecting for this long" />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {isEdit ? 'Save Changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── VLAN dialog ─────────────────────────────────────────────────────────────

function VLANDialog({ open, onClose, onSave, initial }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({ name: '', number: '' });
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) setForm(initial ? { name: initial.name || '', number: String(initial.number || '') } : { name: '', number: '' });
  }, [open, initial]);

  const handleSave = async () => {
    if (!form.name.trim()) { enqueueSnackbar('Name is required', { variant: 'warning' }); return; }
    const num = parseInt(form.number, 10);
    if (!num || num < 1 || num > 4094) { enqueueSnackbar('VLAN number must be 1–4094', { variant: 'warning' }); return; }
    setSaving(true);
    try {
      await onSave({ name: form.name.trim(), number: num }, isEdit ? initial.id : null);
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to save VLAN profile', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? 'Edit VLAN Profile' : 'Create VLAN Profile'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} fullWidth size="small" />
          <TextField label="VLAN Number (1–4094) *" value={form.number} onChange={e => setForm(p => ({ ...p, number: e.target.value }))} type="number" fullWidth size="small" inputProps={{ min: 1, max: 4094 }} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {isEdit ? 'Save Changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Radio Profile dialog ────────────────────────────────────────────────────

function RadioProfileDialog({ open, onClose, onSave, initial }) {
  const isEdit = !!initial?.id;
  const blank = {
    name: '', band: '5GHz', channel: '', channel_width: 'HT80', txpower: '',
    tc_tx_burst_kb: '', tc_tx_latency_ms: '', tc_tx_rate_mb: '',
    tc_rx_burst_kb: '', tc_rx_latency_ms: '', tc_rx_rate_mb: '',
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      if (initial) {
        const tx = initial.traffic_control_tx || {};
        const rx = initial.traffic_control_rx || {};
        setForm({
          ...blank, ...initial,
          tc_tx_burst_kb: tx.burst_kb || '', tc_tx_latency_ms: tx.latency_ms || '', tc_tx_rate_mb: tx.rate_mb || '',
          tc_rx_burst_kb: rx.burst_kb || '', tc_rx_latency_ms: rx.latency_ms || '', tc_rx_rate_mb: rx.rate_mb || '',
        });
      } else {
        setForm(blank);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const widthOptions = form.band === '2.4GHz' ? CHANNEL_WIDTH_OPTIONS_24 : CHANNEL_WIDTH_OPTIONS_5;

  const handleSave = async () => {
    if (!form.name.trim()) { enqueueSnackbar('Name is required', { variant: 'warning' }); return; }
    if (!form.band) { enqueueSnackbar('Band is required', { variant: 'warning' }); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        band: form.band,
        ...(form.channel !== '' && { channel: String(form.channel).trim() }),
        ...(form.channel_width && { channel_width: form.channel_width }),
        ...(form.txpower !== '' && { txpower: String(form.txpower).trim() }),
      };
      const tcTx = {};
      if (form.tc_tx_burst_kb !== '') tcTx.burst_kb = String(form.tc_tx_burst_kb).trim();
      if (form.tc_tx_latency_ms !== '') tcTx.latency_ms = String(form.tc_tx_latency_ms).trim();
      if (form.tc_tx_rate_mb !== '') tcTx.rate_mb = String(form.tc_tx_rate_mb).trim();
      if (Object.keys(tcTx).length > 0) payload.traffic_control_tx = tcTx;

      const tcRx = {};
      if (form.tc_rx_burst_kb !== '') tcRx.burst_kb = String(form.tc_rx_burst_kb).trim();
      if (form.tc_rx_latency_ms !== '') tcRx.latency_ms = String(form.tc_rx_latency_ms).trim();
      if (form.tc_rx_rate_mb !== '') tcRx.rate_mb = String(form.tc_rx_rate_mb).trim();
      if (Object.keys(tcRx).length > 0) payload.traffic_control_rx = tcRx;

      await onSave(payload, isEdit ? initial.id : null);
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to save radio profile', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Radio Profile' : 'Create Radio Profile'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name *" value={form.name} onChange={set('name')} fullWidth size="small" />
          <FormControl fullWidth size="small">
            <InputLabel>Band *</InputLabel>
            <Select value={form.band} label="Band *" onChange={set('band')}>
              {RADIO_BAND_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Channel" value={form.channel} onChange={set('channel')} type="number" size="small"
              helperText="Leave empty for auto" sx={{ flex: 1 }} />
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Channel Width</InputLabel>
              <Select value={form.channel_width} label="Channel Width" onChange={set('channel_width')}>
                {widthOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="TX Power" value={form.txpower} onChange={set('txpower')} type="number" size="small"
              helperText="Leave empty for auto" sx={{ flex: 1 }} />
          </Box>
          <Divider><Typography variant="caption">Traffic Control TX (Download to client)</Typography></Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Rate (Mbps)" value={form.tc_tx_rate_mb} onChange={set('tc_tx_rate_mb')} type="number" size="small" sx={{ flex: 1 }} />
            <Tooltip title="Short-term extra allowance above the rate limit. Lets clients send a quick burst of data without being throttled immediately." arrow placement="top">
              <TextField label="Burst (KB)" value={form.tc_tx_burst_kb} onChange={set('tc_tx_burst_kb')} type="number" size="small" sx={{ flex: 1 }} />
            </Tooltip>
            <Tooltip title="Maximum time packets can wait in the queue before being dropped. Lower values reduce bufferbloat and keep latency predictable." arrow placement="top">
              <TextField label="Latency (ms)" value={form.tc_tx_latency_ms} onChange={set('tc_tx_latency_ms')} type="number" size="small" sx={{ flex: 1 }} />
            </Tooltip>
          </Box>
          <Divider><Typography variant="caption">Traffic Control RX (Upload from client)</Typography></Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Rate (Mbps)" value={form.tc_rx_rate_mb} onChange={set('tc_rx_rate_mb')} type="number" size="small" sx={{ flex: 1 }} />
            <Tooltip title="Short-term extra allowance above the rate limit. Lets clients send a quick burst of data without being throttled immediately." arrow placement="top">
              <TextField label="Burst (KB)" value={form.tc_rx_burst_kb} onChange={set('tc_rx_burst_kb')} type="number" size="small" sx={{ flex: 1 }} />
            </Tooltip>
            <Tooltip title="Maximum time packets can wait in the queue before being dropped. Lower values reduce bufferbloat and keep latency predictable." arrow placement="top">
              <TextField label="Latency (ms)" value={form.tc_rx_latency_ms} onChange={set('tc_rx_latency_ms')} type="number" size="small" sx={{ flex: 1 }} />
            </Tooltip>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {isEdit ? 'Save Changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── RADIUS Profile dialog ────────────────────────────────────────────────────

const IEEE80211W_OPTIONS = [
  { value: '0', label: '0 — Disabled' },
  { value: '1', label: '1 — Optional' },
  { value: '2', label: '2 — Required' },
];

function RADIUSDialog({ open, onClose, onSave, initial }) {
  const isEdit = !!initial?.id;
  const blank = {
    name: '', auth_server: '', auth_port: '1812', auth_secret: '',
    acct_server: '', acct_port: '1813', acct_secret: '', ieee80211w: '0',
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) setForm(initial ? { ...blank, ...initial } : blank);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { enqueueSnackbar('Name is required', { variant: 'warning' }); return; }
    if (!form.auth_server.trim()) { enqueueSnackbar('Auth server is required', { variant: 'warning' }); return; }
    if (!form.auth_port) { enqueueSnackbar('Auth port is required', { variant: 'warning' }); return; }
    if (!form.auth_secret.trim()) { enqueueSnackbar('Auth secret is required', { variant: 'warning' }); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        auth_server: form.auth_server.trim(),
        auth_port: String(form.auth_port).trim(),
        auth_secret: form.auth_secret,
        acct_server: (form.acct_server || '').trim(),
        acct_port: String(form.acct_port || '').trim(),
        acct_secret: form.acct_secret || '',
        ieee80211w: form.ieee80211w || '0',
      };
      await onSave(payload, isEdit ? initial.id : null);
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to save RADIUS profile', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit RADIUS Profile' : 'Create RADIUS Profile'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name *" value={form.name} onChange={set('name')} fullWidth size="small" />
          <Divider><Typography variant="caption">Authentication Server</Typography></Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Auth Server *" value={form.auth_server} onChange={set('auth_server')} size="small" sx={{ flex: 2 }} placeholder="e.g. 10.99.5.7" />
            <TextField label="Auth Port *" value={form.auth_port} onChange={set('auth_port')} type="number" size="small" sx={{ flex: 1 }} />
          </Box>
          <TextField label="Auth Secret *" value={form.auth_secret} onChange={set('auth_secret')} fullWidth size="small" type="password" />
          <Divider><Typography variant="caption">Accounting Server (optional)</Typography></Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Acct Server" value={form.acct_server} onChange={set('acct_server')} size="small" sx={{ flex: 2 }} placeholder="e.g. 10.99.5.7" />
            <TextField label="Acct Port" value={form.acct_port} onChange={set('acct_port')} type="number" size="small" sx={{ flex: 1 }} />
          </Box>
          <TextField label="Acct Secret" value={form.acct_secret} onChange={set('acct_secret')} fullWidth size="small" type="password" />
          <Divider><Typography variant="caption">802.11w (PMF)</Typography></Divider>
          <FormControl fullWidth size="small">
            <InputLabel>IEEE 802.11w</InputLabel>
            <Select value={form.ieee80211w} label="IEEE 802.11w" onChange={set('ieee80211w')}>
              {IEEE80211W_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {isEdit ? 'Save Changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Group dialog ─────────────────────────────────────────────────────────────

function summarizeGroupPatchApplyResults(enqueueSnackbar, res) {
  showApiWarnings(enqueueSnackbar, res);
  const parts = [];
  const summarizeMap = (label, map) => {
    if (!map || typeof map !== 'object') return;
    Object.entries(map).forEach(([profileId, results]) => {
      if (!results || typeof results !== 'object') return;
      let ok = 0;
      let fail = 0;
      let skip = 0;
      Object.values(results).forEach((status) => {
        if (typeof status !== 'string') return;
        if (status === 'success') ok += 1;
        else if (status.startsWith('failed')) fail += 1;
        else skip += 1;
      });
      if (ok + fail + skip === 0) return;
      parts.push(`${label} ${profileId.slice(0, 8)}…: ${ok} ok${fail ? `, ${fail} failed` : ''}${skip ? `, ${skip} skipped` : ''}`);
    });
  };
  summarizeMap('SSID apply', res?.ssid_apply_results);
  summarizeMap('Radio apply', res?.radio_apply_results);
  summarizeMap('SSID remove', res?.ssid_strip_results);
  summarizeMap('Radio remove', res?.radio_strip_results);
  if (parts.length === 0) return;
  const hasFail = parts.some((p) => p.includes('failed'));
  enqueueSnackbar(parts.join(' · '), { variant: hasFail ? 'warning' : 'info' });
}

function GroupDialog({
  open, onClose, onSave, initial, devices, ssidsByDevice = {}, ssids = [],
}) {
  const isEdit = !!initial?.id;
  const [name, setName] = useState('');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [initialSerialSet, setInitialSerialSet] = useState(() => new Set());
  const [addDeviceInput, setAddDeviceInput] = useState('');
  const [treeId, setTreeId] = useState(DEFAULT_CAP_TREE_ID);
  const [capTreeIds, setCapTreeIds] = useState(['5GHz_0', '5GHz_1']);
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      setAddDeviceInput('');
      setTreeId(DEFAULT_CAP_TREE_ID);
      setCapTreeIds(['5GHz_0', '5GHz_1']);
      const initialSerials = new Set(
        (initial?.devices || []).map((s) => String(s).toLowerCase()),
      );
      setInitialSerialSet(initialSerials);
      if (initial?.devices && devices) {
        const preSelected = (initial.devices || [])
          .map(serial => devices.find(d => d.serial_number?.toLowerCase() === serial.toLowerCase()))
          .filter(Boolean);
        setSelectedDevices(preSelected);
      } else {
        setSelectedDevices([]);
      }
    }
  }, [open, initial, devices]);

  const selectedSerialSet = useMemo(
    () => new Set(selectedDevices.map((d) => (d.serial_number || '').toLowerCase())),
    [selectedDevices],
  );

  const devicesAvailableToAdd = useMemo(
    () => (devices || []).filter((d) => !selectedSerialSet.has((d.serial_number || '').toLowerCase())),
    [devices, selectedSerialSet],
  );

  const handleAddDevice = (_, dev) => {
    if (!dev) return;
    const key = (dev.serial_number || '').toLowerCase();
    if (selectedSerialSet.has(key)) return;
    setSelectedDevices((prev) => [...prev, dev]);
    setAddDeviceInput('');
  };

  const handleRemoveDevice = (serial) => {
    const key = String(serial).toLowerCase();
    setSelectedDevices((prev) => prev.filter((d) => (d.serial_number || '').toLowerCase() !== key));
  };

  const groupSsidProfiles = useMemo(() => {
    const ids = new Set((initial?.ssid_ids || []).map(String));
    return (ssids || []).filter((s) => ids.has(s.id));
  }, [initial?.ssid_ids, ssids]);

  const groupHasAssignedProfiles = isEdit && (
    (initial?.ssid_ids?.length > 0) || (initial?.radio_profile_ids?.length > 0)
  );

  const newlyAddedDevices = useMemo(
    () => selectedDevices.filter(
      (d) => !initialSerialSet.has((d.serial_number || '').toLowerCase()),
    ),
    [selectedDevices, initialSerialSet],
  );

  const removedDeviceCount = useMemo(() => {
    const current = new Set(selectedDevices.map((d) => (d.serial_number || '').toLowerCase()));
    let count = 0;
    initialSerialSet.forEach((s) => { if (!current.has(s)) count += 1; });
    return count;
  }, [selectedDevices, initialSerialSet]);

  const inheritedCapTreeId = useMemo(() => {
    for (const dev of selectedDevices) {
      const serial = (dev.serial_number || '').toLowerCase();
      if (!initialSerialSet.has(serial) || !isJnet2CapDevice(dev)) continue;
      for (const profile of groupSsidProfiles) {
        const saved = profile.device_tree_ids?.[serial];
        if (saved) return saved;
      }
    }
    return '';
  }, [selectedDevices, initialSerialSet, groupSsidProfiles]);

  const newlyAddedCapDevices = useMemo(
    () => newlyAddedDevices.filter(isJnet2CapDevice),
    [newlyAddedDevices],
  );

  const needsCapTreeSelect = isEdit
    && newlyAddedCapDevices.length > 0
    && groupSsidProfiles.some((p) => ssidBandNeedsCapTreeSelect(p.band))
    && !inheritedCapTreeId;

  const cap24OnlyBlocked = isEdit
    && newlyAddedCapDevices.length > 0
    && groupSsidProfiles.length > 0
    && groupSsidProfiles.every(
      (p) => String(p.band || '').toLowerCase() === '2.4ghz',
    );

  const capConfigSource = needsCapTreeSelect ? newlyAddedCapDevices[0] : null;

  useEffect(() => {
    if (!capConfigSource) return undefined;
    let cancelled = false;
    setConfigLoading(true);
    api.getDeviceConfig(capConfigSource.serial_number)
      .then((resp) => {
        if (cancelled) return;
        const ids = capUpperTreeIdsFromConfig(resp?.configuration);
        const options = ids.length > 0 ? ids : ['5GHz_0', '5GHz_1'];
        setCapTreeIds(options);
        setTreeId(
          options.includes(DEFAULT_CAP_TREE_ID) ? DEFAULT_CAP_TREE_ID : (options[0] || DEFAULT_CAP_TREE_ID),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setCapTreeIds(['5GHz_0', '5GHz_1']);
          setTreeId(DEFAULT_CAP_TREE_ID);
        }
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, [capConfigSource]);

  const handleSave = async () => {
    if (!name.trim()) { enqueueSnackbar('Group name is required', { variant: 'warning' }); return; }
    if (selectedDevices.length === 0) { enqueueSnackbar('Select at least one device', { variant: 'warning' }); return; }
    if (cap24OnlyBlocked) {
      enqueueSnackbar('Group SSID is 2.4 GHz only — cannot add CAP devices', { variant: 'warning' });
      return;
    }
    if (needsCapTreeSelect && !treeId) {
      enqueueSnackbar('Select a CAP radio slot for the new CAP device(s)', { variant: 'warning' });
      return;
    }
    let deviceTreeIds;
    if (needsCapTreeSelect) {
      deviceTreeIds = Object.fromEntries(
        newlyAddedCapDevices.map((d) => [(d.serial_number || '').toLowerCase(), treeId]),
      );
    }
    setSaving(true);
    try {
      const serials = selectedDevices.map(d => d.serial_number?.toLowerCase()).filter(Boolean);
      await onSave(
        { name: name.trim(), devices: serials },
        isEdit ? initial.id : null,
        { deviceTreeIds },
      );
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to save group', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Group' : 'Create Group'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Group Name *" value={name} onChange={e => setName(e.target.value)} fullWidth size="small" />
          {groupHasAssignedProfiles && newlyAddedDevices.length > 0 && (
            <Alert severity="info" sx={{ py: 0.75 }}>
              Adding {newlyAddedDevices.length} device{newlyAddedDevices.length === 1 ? '' : 's'} will apply this
              group&apos;s assigned SSID and radio profile{initial?.radio_profile_ids?.length !== 1 ? 's' : ''} to
              the new member{newlyAddedDevices.length === 1 ? '' : 's'}.
            </Alert>
          )}
          {groupHasAssignedProfiles && removedDeviceCount > 0 && (
            <Alert severity="warning" sx={{ py: 0.75 }}>
              Removing {removedDeviceCount} device{removedDeviceCount === 1 ? '' : 's'} will strip this group&apos;s
              SSID and radio profile settings from those devices (unless they still receive them via another group or
              a direct assignment).
            </Alert>
          )}
          {needsCapTreeSelect && (
            configLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  New CAP device{newlyAddedCapDevices.length === 1 ? '' : 's'} need a radio slot for the group SSID.
                </Alert>
                <FormControl fullWidth size="small">
                  <InputLabel>CAP radio slot</InputLabel>
                  <Select value={treeId} label="CAP radio slot" onChange={(e) => setTreeId(e.target.value)}>
                    {capTreeIds.map((id) => (
                      <MenuItem key={id} value={id}>{id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )
          )}
          {cap24OnlyBlocked && (
            <Alert severity="warning">Group SSID is 2.4 GHz only — CAP devices cannot be added.</Alert>
          )}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{
              px: 1.5,
              py: 0.75,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Devices ({selectedDevices.length})
              </Typography>
              <Autocomplete
                size="small"
                sx={{ minWidth: 200, flex: '1 1 220px', maxWidth: 360 }}
                options={devicesAvailableToAdd}
                value={null}
                inputValue={addDeviceInput}
                onInputChange={(_, value) => setAddDeviceInput(value)}
                onChange={handleAddDevice}
                getOptionLabel={deviceAutocompleteLabel}
                filterOptions={filterDevicesBySerialOrName}
                isOptionEqualToValue={(a, b) => (
                  (a?.serial_number || '').toLowerCase() === (b?.serial_number || '').toLowerCase()
                )}
                disabled={devicesAvailableToAdd.length === 0}
                renderOption={(props, option) => (
                  <li {...props} key={option.serial_number}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.25 }}>
                      <Typography variant="body2">
                        {deviceAutocompleteLabel(option)}
                        {isJnet2CapDevice(option) ? ' (CAP)' : ''}
                      </Typography>
                      <DeviceDirectSsidIndicator serial={option.serial_number} ssidsByDevice={ssidsByDevice} compact />
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={devicesAvailableToAdd.length === 0 ? 'All devices added' : 'Add device…'}
                    size="small"
                  />
                )}
              />
            </Box>
            {selectedDevices.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1.25, fontSize: '0.85rem' }}>
                No devices in this group. Search above to add devices.
              </Typography>
            ) : (
              selectedDevices.map((dev) => (
                <Box
                  key={dev.serial_number}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    bgcolor: dev.connected ? '#4caf50' : '#f44336' }} />
                  <Box sx={{ minWidth: 0, flex: '1 1 120px' }}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {dev.name || dev.serial_number}
                    </Typography>
                    {dev.name && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {dev.serial_number}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: '1 1 140px', display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
                    <DeviceDirectSsidIndicator serial={dev.serial_number} ssidsByDevice={ssidsByDevice} compact />
                  </Box>
                  <Tooltip title="Remove from group">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveDevice(dev.serial_number)}
                      sx={{ flexShrink: 0 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || cap24OnlyBlocked || configLoading || (needsCapTreeSelect && !treeId)}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isEdit ? 'Save Changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Segment → group dialog ───────────────────────────────────────────────────

function SegmentToGroupDialog({ open, onClose, onCreated }) {
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [saving, setSaving] = useState(false);
  const [creatingAll, setCreatingAll] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!open) {
      setSelectedSegment(null);
      setGroupName('');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api.getCustomTrees()
      .then((res) => {
        if (!cancelled) setTrees(res?.trees || []);
      })
      .catch((err) => {
        if (!cancelled) {
          enqueueSnackbar(err?.message || 'Failed to load segments', { variant: 'error' });
          setTrees([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, enqueueSnackbar]);

  useEffect(() => {
    if (selectedSegment) {
      setGroupName(groupNameFromSegment(selectedSegment));
    } else {
      setGroupName('');
    }
  }, [selectedSegment]);

  const selectedSerials = useMemo(
    () => segmentSerialsFromTree(selectedSegment),
    [selectedSegment],
  );

  const treesWithDevices = useMemo(
    () => trees.filter((t) => segmentSerialsFromTree(t).length > 0),
    [trees],
  );

  const handleCreateOne = async () => {
    if (!selectedSegment) {
      enqueueSnackbar('Select a segment', { variant: 'warning' });
      return;
    }
    const name = groupName.trim();
    if (!name) {
      enqueueSnackbar('Group name is required', { variant: 'warning' });
      return;
    }
    if (selectedSerials.length === 0) {
      enqueueSnackbar('Selected segment has no devices', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await api.createGroup({ name, devices: selectedSerials });
      enqueueSnackbar(`Group "${name}" created (${selectedSerials.length} devices)`, { variant: 'success' });
      onCreated?.();
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to create group', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAll = async () => {
    if (treesWithDevices.length === 0) {
      enqueueSnackbar('No segments with devices found', { variant: 'warning' });
      return;
    }
    setCreatingAll(true);
    let ok = 0;
    let failed = 0;
    const errors = [];
    try {
      for (const tree of treesWithDevices) {
        const name = groupNameFromSegment(tree);
        const serials = segmentSerialsFromTree(tree);
        try {
          await api.createGroup({ name, devices: serials });
          ok += 1;
        } catch (err) {
          failed += 1;
          errors.push(`${name}: ${err?.message || 'failed'}`);
        }
      }
      if (ok > 0) {
        enqueueSnackbar(
          `Created ${ok} group(s) from segments${failed > 0 ? ` (${failed} failed)` : ''}`,
          { variant: failed > 0 ? 'warning' : 'success' },
        );
        onCreated?.();
      }
      if (failed > 0 && ok === 0) {
        enqueueSnackbar(errors.slice(0, 2).join('; '), { variant: 'error' });
      } else if (failed > 0) {
        enqueueSnackbar(errors.slice(0, 2).join('; '), { variant: 'warning' });
      }
      if (ok > 0) onClose();
    } finally {
      setCreatingAll(false);
    }
  };

  const busy = saving || creatingAll;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Segment → Group</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Create a device group from an existing mesh segment (custom tree). Nodes and CAPs in the segment are included.
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : trees.length === 0 ? (
            <Alert severity="info">No segments defined yet. Create segments from the device map first.</Alert>
          ) : (
            <>
              <Autocomplete
                options={trees}
                value={selectedSegment}
                onChange={(_, v) => setSelectedSegment(v)}
                getOptionLabel={(o) => o.name || o.id}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Segment *"
                    placeholder="Select a segment"
                    helperText={
                      selectedSegment
                        ? `Segment ID ${selectedSegment.segment_id} • ${selectedSerials.length} device(s)`
                        : `${trees.length} segment(s) available`
                    }
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Segment ID {option.segment_id} • {segmentSerialsFromTree(option).length} devices
                      </Typography>
                    </Box>
                  </li>
                )}
              />
              <TextField
                label="Group name *"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                fullWidth
                size="small"
                helperText="Defaults to segment name with “ grp” suffix"
              />
              {selectedSerials.length > 0 && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {selectedSerials.length} device{selectedSerials.length === 1 ? '' : 's'} will be added to this group.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="outlined"
          onClick={handleCreateAll}
          disabled={busy || loading || treesWithDevices.length === 0}
          startIcon={creatingAll ? <CircularProgress size={16} /> : <GroupsIcon />}
        >
          {creatingAll ? 'Creating…' : `Create all (${treesWithDevices.length})`}
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateOne}
          disabled={busy || loading || !selectedSegment || selectedSerials.length === 0 || !groupName.trim()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
        >
          Create Group
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Assign SSID profile → one or more groups ────────────────────────────────

const SELECT_ALL_GROUPS_ID = '__select_all_groups__';

function AssignSSIDToGroupsDialog({
  open, onClose, onAssign, items, groups, vlans = [], devices = [],
}) {
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [treeId, setTreeId] = useState(DEFAULT_CAP_TREE_ID);
  const [capTreeIds, setCapTreeIds] = useState(['5GHz_0', '5GHz_1']);
  const [configLoading, setConfigLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const allGroups = useMemo(() => groups || [], [groups]);

  const allGroupsSelected = allGroups.length > 0
    && selectedGroups.length === allGroups.length
    && selectedGroups.every((g) => allGroups.some((ag) => ag.id === g.id));

  const groupOptions = useMemo(() => {
    if (allGroups.length === 0) return [];
    return [
      {
        id: SELECT_ALL_GROUPS_ID,
        name: allGroupsSelected ? 'All groups selected' : `Select all (${allGroups.length})`,
        devices: [],
      },
      ...allGroups,
    ];
  }, [allGroups, allGroupsSelected]);

  const selectedProfile = useMemo(
    () => (items || []).find((item) => item.id === selectedItem) || null,
    [items, selectedItem],
  );

  const groupCapDevices = useMemo(() => {
    if (selectedGroups.length === 0) return [];
    const serialSet = new Set();
    selectedGroups.forEach((g) => {
      (g.devices || []).forEach((s) => serialSet.add(String(s).toLowerCase()));
    });
    return (devices || []).filter(
      (d) => serialSet.has((d.serial_number || '').toLowerCase()) && isJnet2CapDevice(d),
    );
  }, [selectedGroups, devices]);

  const needsCapTreeSelect = groupCapDevices.length > 0
    && selectedProfile
    && ssidBandNeedsCapTreeSelect(selectedProfile.band);

  const cap24OnlyBlocked = groupCapDevices.length > 0
    && selectedProfile
    && String(selectedProfile.band || '').toLowerCase() === '2.4ghz';

  const capConfigSource = needsCapTreeSelect ? groupCapDevices[0] : null;

  useEffect(() => {
    if (open) {
      setSelectedItem('');
      setSelectedGroups([]);
      setTreeId(DEFAULT_CAP_TREE_ID);
      setCapTreeIds(['5GHz_0', '5GHz_1']);
    }
  }, [open]);

  useEffect(() => {
    if (!capConfigSource) return undefined;
    let cancelled = false;
    setConfigLoading(true);
    api.getDeviceConfig(capConfigSource.serial_number)
      .then((resp) => {
        if (cancelled) return;
        const ids = capUpperTreeIdsFromConfig(resp?.configuration);
        const options = ids.length > 0 ? ids : ['5GHz_0', '5GHz_1'];
        setCapTreeIds(options);
        const serial = (capConfigSource.serial_number || '').toLowerCase();
        const saved = selectedProfile?.device_tree_ids?.[serial];
        setTreeId(saved || (options.includes(DEFAULT_CAP_TREE_ID) ? DEFAULT_CAP_TREE_ID : options[0]) || DEFAULT_CAP_TREE_ID);
      })
      .catch(() => {
        if (!cancelled) {
          setCapTreeIds(['5GHz_0', '5GHz_1']);
          setTreeId(DEFAULT_CAP_TREE_ID);
        }
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, [capConfigSource, selectedProfile?.device_tree_ids]);

  const handleGroupsChange = (_, newValue) => {
    if (newValue.some((g) => g.id === SELECT_ALL_GROUPS_ID)) {
      setSelectedGroups(allGroups);
      return;
    }
    setSelectedGroups(newValue);
  };

  const filterGroupOptions = (options, state) => {
    const selectAll = options.find((o) => o.id === SELECT_ALL_GROUPS_ID);
    const groupsOnly = options.filter((o) => o.id !== SELECT_ALL_GROUPS_ID);
    const q = String(state.inputValue || '').trim().toLowerCase();
    const filtered = !q
      ? groupsOnly
      : groupsOnly.filter((g) => (g.name || '').toLowerCase().includes(q));
    return selectAll ? [selectAll, ...filtered] : filtered;
  };

  const handleAssign = async () => {
    if (!selectedItem || selectedGroups.length === 0) {
      enqueueSnackbar('Select an SSID profile and at least one group', { variant: 'warning' });
      return;
    }
    if (cap24OnlyBlocked) {
      enqueueSnackbar('CAP devices have no 2.4 GHz radio', { variant: 'warning' });
      return;
    }
    if (needsCapTreeSelect && !treeId) {
      enqueueSnackbar('Select a CAP radio slot (tree_id)', { variant: 'warning' });
      return;
    }
    let deviceTreeIds;
    if (needsCapTreeSelect) {
      deviceTreeIds = Object.fromEntries(
        groupCapDevices.map((d) => [(d.serial_number || '').toLowerCase(), treeId]),
      );
    }
    const groupIds = selectedGroups.map((g) => g.id).filter(Boolean);
    setAssigning(true);
    try {
      await onAssign(selectedItem, groupIds, { deviceTreeIds });
      enqueueSnackbar(
        `SSID profile assigned to ${groupIds.length} group${groupIds.length === 1 ? '' : 's'}`,
        { variant: 'success' },
      );
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || 'Assignment failed', { variant: 'error' });
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign SSID Profile to Groups</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>SSID Profile</InputLabel>
            <Select value={selectedItem} label="SSID Profile" onChange={(e) => setSelectedItem(e.target.value)}>
              {(items || []).map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {formatSsidProfileOptionLabel(item, vlans)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            options={groupOptions}
            value={selectedGroups}
            onChange={handleGroupsChange}
            filterOptions={filterGroupOptions}
            getOptionLabel={(g) => (
              g.id === SELECT_ALL_GROUPS_ID
                ? g.name
                : `${g.name} (${(g.devices || []).length} devices)`
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            getOptionDisabled={(option) => (
              option.id === SELECT_ALL_GROUPS_ID && (allGroups.length === 0 || allGroupsSelected)
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Typography
                  variant="body2"
                  fontWeight={option.id === SELECT_ALL_GROUPS_ID ? 600 : 400}
                  color={option.id === SELECT_ALL_GROUPS_ID ? 'primary' : 'text.primary'}
                >
                  {option.id === SELECT_ALL_GROUPS_ID
                    ? option.name
                    : `${option.name} (${(option.devices || []).length} devices)`}
                </Typography>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((group, index) => (
                <Chip
                  key={group.id}
                  label={group.name}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Groups *"
                placeholder="Select groups…"
                size="small"
              />
            )}
          />
          {needsCapTreeSelect && groupCapDevices.length > 0 && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Selected groups include {groupCapDevices.length} CAP{groupCapDevices.length === 1 ? '' : 's'}.
              Pick the upper-band radio slot for all CAPs.
            </Alert>
          )}
          {cap24OnlyBlocked && (
            <Alert severity="warning">
              This SSID profile is 2.4 GHz only. CAP devices in the selected groups have no 2.4 GHz radio.
            </Alert>
          )}
          {needsCapTreeSelect && (
            configLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>CAP radio slot</InputLabel>
                  <Select value={treeId} label="CAP radio slot" onChange={(e) => setTreeId(e.target.value)}>
                    {capTreeIds.map((id) => (
                      <MenuItem key={id} value={id}>{id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Assigning on <strong>{treeId || 'selected slot'}</strong> for all{' '}
                  <strong>{groupCapDevices.length}</strong> CAP{groupCapDevices.length === 1 ? '' : 's'} sets
                  treemgmt <code>max_children</code> to 0 on that radio.
                </Alert>
              </>
            )
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={assigning}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={
            assigning
            || !selectedItem
            || selectedGroups.length === 0
            || cap24OnlyBlocked
            || configLoading
            || (needsCapTreeSelect && !treeId)
          }
          startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : <AssignmentIcon />}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Assign radio profile → one or more groups ───────────────────────────────

function AssignRadioToGroupsDialog({ open, onClose, onAssign, items, groups }) {
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const allGroups = useMemo(() => groups || [], [groups]);

  const allGroupsSelected = allGroups.length > 0
    && selectedGroups.length === allGroups.length
    && selectedGroups.every((g) => allGroups.some((ag) => ag.id === g.id));

  const groupOptions = useMemo(() => {
    if (allGroups.length === 0) return [];
    return [
      {
        id: SELECT_ALL_GROUPS_ID,
        name: allGroupsSelected ? 'All groups selected' : `Select all (${allGroups.length})`,
        devices: [],
      },
      ...allGroups,
    ];
  }, [allGroups, allGroupsSelected]);

  useEffect(() => {
    if (open) {
      setSelectedItem('');
      setSelectedGroups([]);
    }
  }, [open]);

  const handleGroupsChange = (_, newValue) => {
    if (newValue.some((g) => g.id === SELECT_ALL_GROUPS_ID)) {
      setSelectedGroups(allGroups);
      return;
    }
    setSelectedGroups(newValue);
  };

  const filterGroupOptions = (options, state) => {
    const selectAll = options.find((o) => o.id === SELECT_ALL_GROUPS_ID);
    const groupsOnly = options.filter((o) => o.id !== SELECT_ALL_GROUPS_ID);
    const q = String(state.inputValue || '').trim().toLowerCase();
    const filtered = !q
      ? groupsOnly
      : groupsOnly.filter((g) => (g.name || '').toLowerCase().includes(q));
    return selectAll ? [selectAll, ...filtered] : filtered;
  };

  const handleAssign = async () => {
    if (!selectedItem || selectedGroups.length === 0) {
      enqueueSnackbar('Select a radio profile and at least one group', { variant: 'warning' });
      return;
    }
    const groupIds = selectedGroups.map((g) => g.id).filter(Boolean);
    setAssigning(true);
    try {
      await onAssign(selectedItem, groupIds);
      enqueueSnackbar(
        `Radio profile assigned to ${groupIds.length} group${groupIds.length === 1 ? '' : 's'}`,
        { variant: 'success' },
      );
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || 'Assignment failed', { variant: 'error' });
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Radio Profile to Groups</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Radio Profile</InputLabel>
            <Select value={selectedItem} label="Radio Profile" onChange={(e) => setSelectedItem(e.target.value)}>
              {(items || []).map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name || `#${item.number}`}
                  {item.band ? ` (${formatRadioProfileBand(item.band)})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            options={groupOptions}
            value={selectedGroups}
            onChange={handleGroupsChange}
            filterOptions={filterGroupOptions}
            getOptionLabel={(g) => (
              g.id === SELECT_ALL_GROUPS_ID
                ? g.name
                : `${g.name} (${(g.devices || []).length} devices)`
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            getOptionDisabled={(option) => (
              option.id === SELECT_ALL_GROUPS_ID && (allGroups.length === 0 || allGroupsSelected)
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Typography
                  variant="body2"
                  fontWeight={option.id === SELECT_ALL_GROUPS_ID ? 600 : 400}
                  color={option.id === SELECT_ALL_GROUPS_ID ? 'primary' : 'text.primary'}
                >
                  {option.id === SELECT_ALL_GROUPS_ID
                    ? option.name
                    : `${option.name} (${(option.devices || []).length} devices)`}
                </Typography>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((group, index) => (
                <Chip
                  key={group.id}
                  label={group.name}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Groups *"
                placeholder="Select groups…"
                size="small"
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={assigning}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={assigning || !selectedItem || selectedGroups.length === 0}
          startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : <AssignmentIcon />}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Assign SSID / radio profile → one or more devices (CAP / NODE, not mixed) ─

function AssignProfileToDevicesDialog({
  open, onClose, onAssign, title, items, itemLabel, devices, profileKind = 'ssid', vlans = [],
}) {
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [treeId, setTreeId] = useState('');
  const [capTreeIds, setCapTreeIds] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const profileTypeLabel = profileKind === 'radio' ? 'Radio profile' : 'SSID profile';

  const selectedProfile = useMemo(
    () => (items || []).find((item) => item.id === selectedItem) || null,
    [items, selectedItem],
  );

  const capDevicesInList = useMemo(
    () => (devices || []).filter(isJnet2CapDevice),
    [devices],
  );

  const selectionKind = useMemo(() => {
    if (selectedDevices.length === 0) return null;
    return isJnet2CapDevice(selectedDevices[0]) ? 'cap' : 'node';
  }, [selectedDevices]);

  const allSelectedAreCaps = selectionKind === 'cap' && selectedDevices.length > 0;

  const needsCapTreeSelect = useMemo(() => {
    if (!allSelectedAreCaps || !selectedProfile) return false;
    if (profileKind === 'radio') {
      return radioProfileNeedsCapTreeSelect(selectedProfile.band);
    }
    return ssidBandNeedsCapTreeSelect(selectedProfile.band);
  }, [allSelectedAreCaps, selectedProfile, profileKind]);

  const cap24OnlyBlocked = useMemo(() => (
    profileKind === 'ssid'
    && allSelectedAreCaps
    && selectedProfile
    && String(selectedProfile.band || '').toLowerCase() === '2.4ghz'
  ), [profileKind, allSelectedAreCaps, selectedProfile]);

  const capConfigSource = useMemo(
    () => (allSelectedAreCaps ? selectedDevices[0] : null),
    [allSelectedAreCaps, selectedDevices],
  );

  const allCapsSelected = capDevicesInList.length > 0
    && selectedDevices.length === capDevicesInList.length
    && selectedDevices.every((d) => isJnet2CapDevice(d));

  useEffect(() => {
    if (open) {
      setSelectedItem('');
      setSelectedDevices([]);
      setTreeId('');
      setCapTreeIds([]);
    }
  }, [open]);

  useEffect(() => {
    if (!capConfigSource) {
      setTreeId('');
      setCapTreeIds([]);
      return undefined;
    }
    let cancelled = false;
    setConfigLoading(true);
    api.getDeviceConfig(capConfigSource.serial_number)
      .then((resp) => {
        if (cancelled) return;
        const ids = capUpperTreeIdsFromConfig(resp?.configuration);
        setCapTreeIds(ids);
        const serial = (capConfigSource.serial_number || '').toLowerCase();
        const saved = selectedProfile?.device_tree_ids?.[serial];
        setTreeId(saved || ids[0] || '');
      })
      .catch(() => {
        if (!cancelled) {
          setCapTreeIds([]);
          setTreeId('');
        }
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, [capConfigSource, selectedProfile?.device_tree_ids]);

  const handleDevicesChange = (_, newValue) => {
    if (!newValue?.length) {
      setSelectedDevices([]);
      return;
    }
    const hasCap = newValue.some(isJnet2CapDevice);
    const hasNode = newValue.some((d) => !isJnet2CapDevice(d));
    if (hasCap && hasNode) {
      enqueueSnackbar(
        `Cannot mix CAP and NODE devices in one ${profileKind === 'radio' ? 'radio' : 'SSID'} assignment`,
        { variant: 'warning' },
      );
      return;
    }
    setSelectedDevices(newValue);
  };

  const handleSelectAllCaps = () => {
    if (selectionKind === 'node') {
      enqueueSnackbar('Clear NODE selection before selecting all CAPs', { variant: 'warning' });
      return;
    }
    if (capDevicesInList.length === 0) {
      enqueueSnackbar('No CAP devices available', { variant: 'info' });
      return;
    }
    if (cap24OnlyBlocked) {
      enqueueSnackbar('CAP devices have no 2.4 GHz radio', { variant: 'warning' });
      return;
    }
    setSelectedDevices(capDevicesInList);
  };

  const handleAssign = async () => {
    if (!selectedItem || selectedDevices.length === 0) {
      enqueueSnackbar(`Select a ${profileTypeLabel.toLowerCase()} and at least one device`, { variant: 'warning' });
      return;
    }
    if (cap24OnlyBlocked) {
      enqueueSnackbar('CAP devices have no 2.4 GHz radio', { variant: 'warning' });
      return;
    }
    if (needsCapTreeSelect && !treeId) {
      enqueueSnackbar('Select a CAP radio slot (tree_id)', { variant: 'warning' });
      return;
    }
    const serials = selectedDevices
      .map((d) => (d.serial_number || '').toLowerCase())
      .filter(Boolean);
    let deviceTreeIds;
    if (needsCapTreeSelect) {
      deviceTreeIds = Object.fromEntries(serials.map((s) => [s, treeId]));
    }
    setAssigning(true);
    try {
      await onAssign(selectedItem, serials, { deviceTreeIds });
      enqueueSnackbar(
        `${profileTypeLabel} assigned to ${serials.length} device${serials.length === 1 ? '' : 's'}`,
        { variant: 'success' },
      );
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || 'Assignment failed', { variant: 'error' });
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>{itemLabel}</InputLabel>
            <Select value={selectedItem} label={itemLabel} onChange={(e) => setSelectedItem(e.target.value)}>
              {(items || []).map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {profileKind === 'ssid'
                    ? formatSsidProfileOptionLabel(item, vlans)
                    : `${item.name || `#${item.number}`}${item.band ? ` (${item.band})` : ''}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {capDevicesInList.length} CAP{capDevicesInList.length === 1 ? '' : 's'} in list
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectAllCaps}
              disabled={
                capDevicesInList.length === 0
                || selectionKind === 'node'
                || cap24OnlyBlocked
                || allCapsSelected
              }
            >
              {allCapsSelected ? 'All CAPs selected' : `Select all CAPs (${capDevicesInList.length})`}
            </Button>
          </Box>
          <Autocomplete
            multiple
            options={devices || []}
            value={selectedDevices}
            onChange={handleDevicesChange}
            getOptionLabel={deviceAutocompleteLabel}
            filterOptions={filterDevicesBySerialOrName}
            isOptionEqualToValue={(a, b) => (
              (a?.serial_number || '').toLowerCase() === (b?.serial_number || '').toLowerCase()
            )}
            getOptionDisabled={(option) => {
              if (!selectionKind) return false;
              return selectionKind === 'cap'
                ? !isJnet2CapDevice(option)
                : isJnet2CapDevice(option);
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.serial_number}>
                {deviceAutocompleteLabel(option)}
                {isJnet2CapDevice(option) ? ' (CAP)' : ''}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.serial_number}
                  label={`${option.name || option.serial_number}${isJnet2CapDevice(option) ? ' (CAP)' : ''}`}
                  size="small"
                  color={option.connected ? 'success' : 'default'}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Devices *"
                placeholder="Search by serial…"
                size="small"
                helperText={
                  selectionKind === 'cap'
                    ? 'CAP only — one radio slot applies to all selected CAPs'
                    : selectionKind === 'node'
                      ? 'NODE only — CAP devices cannot be added to this selection'
                      : 'Select CAPs or NODEs (do not mix both types)'
                }
              />
            )}
          />
          {cap24OnlyBlocked && (
            <Alert severity="warning">
              This SSID profile is 2.4 GHz only. CAP devices have no 2.4 GHz radio.
            </Alert>
          )}
          {needsCapTreeSelect && (
            configLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>CAP radio slot</InputLabel>
                  <Select value={treeId} label="CAP radio slot" onChange={(e) => setTreeId(e.target.value)}>
                    {capTreeIds.map((id) => (
                      <MenuItem key={id} value={id}>{id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Assigning on <strong>{treeId || 'selected slot'}</strong> for all{' '}
                  <strong>{selectedDevices.length}</strong> CAP{selectedDevices.length === 1 ? '' : 's'} sets
                  treemgmt <code>max_children</code> to 0 on that radio.
                </Alert>
              </>
            )
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={assigning}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={
            assigning
            || !selectedItem
            || selectedDevices.length === 0
            || cap24OnlyBlocked
            || configLoading
            || (needsCapTreeSelect && !treeId)
          }
          startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : <AssignmentIcon />}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Assign VLAN to SSID dialog ───────────────────────────────────────────────

function AssignVLANToSSIDDialog({ open, ssid, vlans, onClose, onAssign }) {
  const [selectedVLAN, setSelectedVLAN] = useState('');
  const [assigning, setAssigning] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => { if (open) setSelectedVLAN(ssid?.vlan_profile_id || ''); }, [open, ssid]);

  const handleAssign = async () => {
    if (!selectedVLAN) { enqueueSnackbar('Select a VLAN profile', { variant: 'warning' }); return; }
    setAssigning(true);
    try {
      await onAssign(selectedVLAN, ssid.id);
      enqueueSnackbar(`VLAN assigned to SSID "${ssid.ssid}"`, { variant: 'success' });
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || 'Assignment failed', { variant: 'error' });
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Assign VLAN to SSID "{ssid?.ssid}"</DialogTitle>
      <DialogContent>
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>VLAN Profile</InputLabel>
          <Select value={selectedVLAN} label="VLAN Profile" onChange={e => setSelectedVLAN(e.target.value)}>
            {(vlans || []).map(v => (
              <MenuItem key={v.id} value={v.id}>{v.name} (#{v.number})</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={assigning}>Cancel</Button>
        <Button variant="contained" onClick={handleAssign} disabled={assigning || !selectedVLAN}
          startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : <AssignmentIcon />}>
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function GroupsManager({ devices, onHighlightGroupOnMap, isActive, readOnly = false }) {
  const { enqueueSnackbar } = useSnackbar();
  const canMutate = !readOnly;
  const [tab, setTab] = useState(0);
  const [profileTab, setProfileTab] = useState(0); // sub-tabs inside Profiles

  // Groups state
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupDialog, setGroupDialog] = useState({ open: false, initial: null });
  const [segmentToGroupDialogOpen, setSegmentToGroupDialogOpen] = useState(false);
  const [assignSSIDDialog, setAssignSSIDDialog] = useState(false);
  const [assignRadioToGroupDialog, setAssignRadioToGroupDialog] = useState(false);
  const [assignVLANToSSIDDialog, setAssignVLANToSSIDDialog] = useState({ open: false, ssid: null });
  const [expandedGroup, setExpandedGroup] = useState(null);

  // SSID profiles state
  const [ssids, setSsids] = useState([]);
  const [ssidsLoading, setSsidsLoading] = useState(false);
  const [ssidDialog, setSSIDDialog] = useState({ open: false, initial: null });

  // VLAN profiles state
  const [vlans, setVlans] = useState([]);
  const [vlansLoading, setVlansLoading] = useState(false);
  const [vlanDialog, setVLANDialog] = useState({ open: false, initial: null });

  // Radio profiles state
  const [radioProfiles, setRadioProfiles] = useState([]);
  const [radioProfilesLoading, setRadioProfilesLoading] = useState(false);
  const [radioDialog, setRadioDialog] = useState({ open: false, initial: null });

  // RADIUS profiles state
  const [radiusProfiles, setRadiusProfiles] = useState([]);
  const [radiusProfilesLoading, setRadiusProfilesLoading] = useState(false);
  const [radiusDialog, setRadiusDialog] = useState({ open: false, initial: null });

  // Device-level assignment dialogs
  const [assignSSIDToDeviceDialog, setAssignSSIDToDeviceDialog] = useState(false);
  const [assignRadioToDeviceDialog, setAssignRadioToDeviceDialog] = useState(false);

  // Syslog tab state
  const [syslogScope, setSyslogScope] = useState('device'); // 'group' | 'device'
  const [syslogSelectedGroups, setSyslogSelectedGroups] = useState([]); // array of group objects
  const [syslogSelectedDevices, setSyslogSelectedDevices] = useState([]); // array of device objects or [{serial_number:'__ALL__'}]
  const [syslogCfg, setSyslogCfg] = useState({ ip: '', port: '', proto: 'udp', log_remote: '' });
  const [syslogSaving, setSyslogSaving] = useState(false);
  const [syslogClearing, setSyslogClearing] = useState(false);
  const [syslogStatus, setSyslogStatus] = useState(null);

  // Devices tab pagination, sorting & search
  const [devPage, setDevPage] = useState(0);
  const [devRowsPerPage, setDevRowsPerPage] = useState(20);
  const [devSortCol, setDevSortCol] = useState('device');
  const [devSortDir, setDevSortDir] = useState('asc');
  const [devSearchQuery, setDevSearchQuery] = useState('');
  const [rrmPlanData, setRrmPlanData] = useState(null);

  const isJnet2WifiDevice = useCallback((dev) => {
    if (!dev?.capabilities) return false;
    const model = (dev.capabilities.model || '').toLowerCase();
    if (model.includes('nvidia') || model.includes('jetson') || model.includes('qrb5165')) return false;
    if (model.includes('eap102') || model.includes('edgecore')) return false;
    return dev.compatible === 'jnet2';
  }, []);

  const jnet2WifiDevices = useMemo(
    () => (devices || []).filter(isJnet2WifiDevice),
    [devices, isJnet2WifiDevice],
  );

  useEffect(() => {
    if (!isActive) return undefined;
    let cancelled = false;
    api.getPublicWifiRRMPlan()
      .then((data) => {
        if (!cancelled && data) setRrmPlanData(data);
      })
      .catch((err) => {
        if (!cancelled && err?.statusCode !== 404) console.warn('Wi‑Fi RRM plan:', err);
      });
    return () => { cancelled = true; };
  }, [isActive]);

  const rrmPlanIndex = useMemo(() => buildRrmPlanIndex(rrmPlanData), [rrmPlanData]);

  const devicesForDirectWifiAssign = useMemo(() => jnet2WifiDevices, [jnet2WifiDevices]);

  const ssidsByDevice = useMemo(() => buildSsidsByDevice(ssids), [ssids]);

  // ── fetch helpers ─────────────────────────────────────────────────────────

  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const res = await api.getGroups(true);
      setGroups(res?.groups || []);
    } catch (err) {
      enqueueSnackbar('Failed to load groups', { variant: 'error' });
    } finally {
      setGroupsLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchSSIDs = useCallback(async () => {
    setSsidsLoading(true);
    try {
      const res = await api.getSSIDProfiles();
      setSsids(res?.ssids || []);
    } catch (err) {
      enqueueSnackbar('Failed to load SSID profiles', { variant: 'error' });
    } finally {
      setSsidsLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchVLANs = useCallback(async () => {
    setVlansLoading(true);
    try {
      const res = await api.getVLANProfiles();
      setVlans(res?.vlans || []);
    } catch (err) {
      enqueueSnackbar('Failed to load VLAN profiles', { variant: 'error' });
    } finally {
      setVlansLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchRadioProfiles = useCallback(async () => {
    setRadioProfilesLoading(true);
    try {
      const res = await api.getRadioProfiles();
      setRadioProfiles(res?.radio_profiles || []);
    } catch (err) {
      enqueueSnackbar('Failed to load radio profiles', { variant: 'error' });
    } finally {
      setRadioProfilesLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchRadiusProfiles = useCallback(async () => {
    setRadiusProfilesLoading(true);
    try {
      const res = await api.getRadiusProfiles();
      setRadiusProfiles(res?.radius_profiles || []);
    } catch (err) {
      enqueueSnackbar('Failed to load RADIUS profiles', { variant: 'error' });
    } finally {
      setRadiusProfilesLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { fetchGroups(); fetchSSIDs(); fetchVLANs(); fetchRadioProfiles(); fetchRadiusProfiles(); }, [fetchGroups, fetchSSIDs, fetchVLANs, fetchRadioProfiles, fetchRadiusProfiles]);
  useEffect(() => { if (isActive) { fetchGroups(); fetchRadioProfiles(); } }, [isActive, fetchGroups, fetchRadioProfiles]);

  // Per-group "more actions" menu anchor
  const [groupMenu, setGroupMenu] = useState({ anchorEl: null, group: null });

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, item: null });
  const [deleting, setDeleting] = useState(false);

  const handleDeleteConfirmed = async () => {
    const { type, item } = deleteConfirm;
    setDeleting(true);
    try {
      if (type === 'group') {
        await api.deleteGroup(item.id);
        enqueueSnackbar(`Group "${item.name}" deleted`, { variant: 'success' });
        fetchGroups();
      } else if (type === 'ssid') {
        await api.deleteSSIDProfile(item.id);
        enqueueSnackbar(`SSID profile "${item.ssid}" deleted`, { variant: 'success' });
        fetchSSIDs();
        fetchGroups();
      } else if (type === 'vlan') {
        await api.deleteVLANProfile(item.id);
        enqueueSnackbar(`VLAN profile "${item.name}" deleted`, { variant: 'success' });
        fetchVLANs();
        fetchGroups();
      } else if (type === 'radio') {
        await api.deleteRadioProfile(item.id);
        enqueueSnackbar(`Radio profile "${item.name}" deleted`, { variant: 'success' });
        fetchRadioProfiles();
        fetchGroups();
      } else if (type === 'radius') {
        await api.deleteRadiusProfile(item.id);
        enqueueSnackbar(`RADIUS profile "${item.name}" deleted`, { variant: 'success' });
        fetchRadiusProfiles();
      }
      setDeleteConfirm({ open: false, type: null, item: null });
    } catch (err) {
      enqueueSnackbar(err.message || 'Delete failed', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  // ── group actions ──────────────────────────────────────────────────────────

  const handleSaveGroup = async (data, id, { deviceTreeIds } = {}) => {
    if (id) {
      const current = groups.find(g => g.id === id);
      const currentDevices = new Set((current?.devices || []).map(s => String(s).toLowerCase()));
      const nextDevices = new Set((data.devices || []).map(s => String(s).toLowerCase()));

      const addDevices = [...nextDevices].filter(s => !currentDevices.has(s));
      const removeDevices = [...currentDevices].filter(s => !nextDevices.has(s));

      const patch = {};
      if (!current || (data.name && data.name !== current.name)) {
        patch.group_name = data.name;
      }
      if (addDevices.length > 0) patch.add_devices = addDevices;
      if (removeDevices.length > 0) patch.remove_devices = removeDevices;
      if (deviceTreeIds && Object.keys(deviceTreeIds).length > 0) {
        patch.device_tree_ids = deviceTreeIds;
      }

      if (Object.keys(patch).length === 0) {
        enqueueSnackbar('No group changes to save', { variant: 'info' });
        return;
      }

      const res = await api.updateGroup(id, patch);
      enqueueSnackbar('Group updated', { variant: 'success' });
      summarizeGroupPatchApplyResults(enqueueSnackbar, res);
    } else {
      await api.createGroup(data);
      enqueueSnackbar('Group created', { variant: 'success' });
    }
    fetchGroups();
  };

  // ── SSID actions ───────────────────────────────────────────────────────────

  const handleSaveSSID = async (data, id) => {
    const payload = { ...data, vlan_profile_id: data.vlan_profile_id || '', radius_profile_id: data.radius_profile_id || '' };
    if (id) {
      const res = await api.updateSSIDProfile(id, payload);
      enqueueSnackbar('SSID profile updated', { variant: 'success' });
      showApiWarnings(enqueueSnackbar, res);
    } else {
      await api.createSSIDProfile(payload);
      enqueueSnackbar('SSID profile created', { variant: 'success' });
    }
    fetchSSIDs();
  };

  const handleAssignSSIDToGroups = async (ssidId, groupIds, { deviceTreeIds } = {}) => {
    try {
      const res = groupIds.length === 1
        ? await api.assignSSIDToGroup(ssidId, groupIds[0], { deviceTreeIds })
        : await api.assignSSIDToGroups(ssidId, groupIds, { deviceTreeIds });
      showApiWarnings(enqueueSnackbar, res);
    } finally {
      fetchGroups();
      fetchSSIDs();
    }
  };

  const handleUnassignSSID = async (ssidId, groupId) => {
    try {
      await api.unassignSSIDFromGroup(ssidId, groupId);
      enqueueSnackbar('SSID removed from group', { variant: 'success' });
      fetchGroups();
      fetchSSIDs();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to unassign SSID', { variant: 'error' });
    }
  };

  // ── VLAN actions ───────────────────────────────────────────────────────────

  const handleSaveVLAN = async (data, id) => {
    if (id) {
      await api.updateVLANProfile(id, data);
      enqueueSnackbar('VLAN profile updated', { variant: 'success' });
    } else {
      await api.createVLANProfile(data);
      enqueueSnackbar('VLAN profile created', { variant: 'success' });
    }
    fetchVLANs();
  };

  const handleAssignVLANToSSID = async (vlanId, ssidId) => {
    const res = await api.assignVLANToSSID(ssidId, vlanId);
    showApiWarnings(enqueueSnackbar, res);
    fetchSSIDs();
  };

  // ── Radio profile actions ──────────────────────────────────────────────────

  const handleSaveRadioProfile = async (data, id) => {
    if (id) {
      const res = await api.updateRadioProfile(id, data);
      enqueueSnackbar('Radio profile updated', { variant: 'success' });
      showApiWarnings(enqueueSnackbar, res);
    } else {
      await api.createRadioProfile(data);
      enqueueSnackbar('Radio profile created', { variant: 'success' });
    }
    fetchRadioProfiles();
  };

  const handleAssignRadioToGroups = async (profileId, groupIds) => {
    const res = groupIds.length === 1
      ? await api.assignRadioProfileToGroup(profileId, groupIds[0])
      : await api.assignRadioProfileToGroups(profileId, groupIds);
    showApiWarnings(enqueueSnackbar, res);
    fetchGroups();
    fetchRadioProfiles();
  };

  const handleUnassignRadioFromGroup = async (profileId, groupId) => {
    try {
      await api.unassignRadioProfileFromGroup(profileId, groupId);
      enqueueSnackbar('Radio profile removed from group', { variant: 'success' });
      fetchGroups();
      fetchRadioProfiles();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to unassign radio profile', { variant: 'error' });
    }
  };

  // ── RADIUS profile actions ─────────────────────────────────────────────────

  const handleSaveRadiusProfile = async (data, id) => {
    if (id) {
      await api.updateRadiusProfile(id, data);
      enqueueSnackbar('RADIUS profile updated', { variant: 'success' });
    } else {
      await api.createRadiusProfile(data);
      enqueueSnackbar('RADIUS profile created', { variant: 'success' });
    }
    fetchRadiusProfiles();
  };

  const handleUnassignRadiusFromSSID = async (ssidId) => {
    try {
      await api.unassignRadiusFromSSID(ssidId);
      enqueueSnackbar('RADIUS profile removed from SSID', { variant: 'success' });
      fetchSSIDs();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to unassign RADIUS profile', { variant: 'error' });
    }
  };

  // ── Device-level assignment actions ────────────────────────────────────────

  const handleAssignSSIDToDevices = async (ssidId, serials, { deviceTreeIds } = {}) => {
    try {
      const res = serials.length === 1
        ? await api.assignSSIDToDevice(ssidId, serials[0], { treeId: deviceTreeIds?.[serials[0]] })
        : await api.assignSSIDToDevices(ssidId, serials, { deviceTreeIds });
      showApiWarnings(enqueueSnackbar, res);
    } finally {
      fetchSSIDs();
    }
  };

  const handleUnassignSSIDFromDevice = async (ssidId, serial) => {
    try {
      await api.unassignSSIDFromDevice(ssidId, serial);
      enqueueSnackbar('SSID removed from device', { variant: 'success' });
      fetchSSIDs();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to unassign SSID from device', { variant: 'error' });
    }
  };

  const handleAssignRadioToDevices = async (profileId, serials, { deviceTreeIds } = {}) => {
    const res = serials.length === 1
      ? await api.assignRadioProfileToDevice(profileId, serials[0], { treeId: deviceTreeIds?.[serials[0]] })
      : await api.assignRadioProfileToDevices(profileId, serials, { deviceTreeIds });
    showApiWarnings(enqueueSnackbar, res);
    fetchRadioProfiles();
  };

  const handleUnassignRadioFromDevice = async (profileId, serial) => {
    try {
      await api.unassignRadioProfileFromDevice(profileId, serial);
      enqueueSnackbar('Radio profile removed from device', { variant: 'success' });
      fetchRadioProfiles();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to unassign radio profile from device', { variant: 'error' });
    }
  };

  // ── highlight on map ───────────────────────────────────────────────────────

  const handleViewOnMap = (group) => {
    if (onHighlightGroupOnMap) {
      onHighlightGroupOnMap(group.devices || []);
    }
  };

  // ── render: Devices tab ───────────────────────────────────────────────────

  const renderDevicesTab = () => {
    const deviceList = jnet2WifiDevices;

    const renderRrmPlanCell = (dev) => {
      if (isJnet2CapDevice(dev)) {
        return (
          <Tooltip title="CAP device — assign Public WiFi on 5GHz_0 or 5GHz_1 via Assign SSID / Assign Radio.">
            <Chip label="CAP" size="small" color="secondary" variant="outlined" sx={{ fontWeight: 600 }} />
          </Tooltip>
        );
      }
      if (!rrmPlanIndex.hasData) {
        return (
          <Tooltip title="No stored Wi‑Fi RRM plan yet. Generate one from the Wi‑Fi RRM page under WiFi Networks.">
            <Typography variant="caption" color="text.secondary">—</Typography>
          </Tooltip>
        );
      }
      const role = getRrmDevicePlanRole(dev.serial_number, {
        excludedSerialSet: rrmPlanIndex.excludedSerialSet,
        planSerialSet: rrmPlanIndex.planSerialSet,
        deviceHas5GHzPublicWifi: has5GHzPublicWifi(dev),
      });
      const wrap = (node, title) => (
        <Tooltip title={title} placement="left">
          <span>{node}</span>
        </Tooltip>
      );
      if (role === 'planned') {
        return wrap(
          <Chip label="Planned AP" size="small" color="default" variant="outlined" sx={{ fontWeight: 600 }} />,
          'In the minimal 5 GHz Public WiFi deployment plan — recommended AP location.',
        );
      }
      if (role === 'optional') {
        return wrap(
          <Chip label="Optional" size="small" color="default" variant="outlined" sx={{ fontWeight: 600 }} />,
          'Not in the minimal plan — may assign Public WiFi for extra capacity or coverage.',
        );
      }
      if (role === 'critical') {
        return wrap(
          <Chip label="Critical" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />,
          'Critical tree parent — not in the minimal 5 GHz plan. Assigning Public WiFi is allowed but may affect mesh backhaul.',
        );
      }
      if (role === 'conflict') {
        return wrap(
          <Chip label="Conflict" size="small" color="error" sx={{ fontWeight: 600 }} />,
          'Excluded as critical parent but 5 GHz Public WiFi is active — remove 5 GHz Public WiFi from this NODE.',
        );
      }
      return '—';
    };

    const ssidsByDevice = {};
    ssids.forEach(s => {
      (s.device_serials || []).forEach(serial => {
        const key = serial.toLowerCase();
        if (!ssidsByDevice[key]) ssidsByDevice[key] = [];
        ssidsByDevice[key].push(s);
      });
    });

    const radioByDevice = {};
    radioProfiles.forEach(rp => {
      (rp.device_serials || []).forEach(serial => {
        const key = serial.toLowerCase();
        if (!radioByDevice[key]) radioByDevice[key] = [];
        radioByDevice[key].push(rp);
      });
    });

    // serial (lower-case) → { groupName, ssids: string[] }
    const groupByDevice = {};
    (groups || []).forEach((g) => {
      const ssidNames = (g.ssids || []).map((s) => s.ssid).filter(Boolean);
      (g.devices || []).forEach((serial) => {
        groupByDevice[serial.toLowerCase()] = { groupName: g.name || '—', ssids: ssidNames };
      });
    });

    const handleSort = (col) => {
      if (devSortCol === col) {
        setDevSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setDevSortCol(col);
        setDevSortDir('asc');
      }
      setDevPage(0);
    };

    const filteredDevices = deviceList.filter((d) => deviceMatchesSearch(d, devSearchQuery));

    const sortedDevices = [...filteredDevices].sort((a, b) => {
      const dir = devSortDir === 'asc' ? 1 : -1;
      const sA = a.serial_number?.toLowerCase() || '';
      const sB = b.serial_number?.toLowerCase() || '';
      switch (devSortCol) {
        case 'device': {
          const nA = (a.name || a.serial_number || '').toLowerCase();
          const nB = (b.name || b.serial_number || '').toLowerCase();
          return nA.localeCompare(nB) * dir;
        }
        case 'status':
          return ((a.connected ? 0 : 1) - (b.connected ? 0 : 1)) * dir;
        case 'group': {
          const gA = (groupByDevice[sA]?.groupName || '').toLowerCase();
          const gB = (groupByDevice[sB]?.groupName || '').toLowerCase();
          return gA.localeCompare(gB) * dir;
        }
        case 'ssids': {
          const cA = (ssidsByDevice[sA] || []).length;
          const cB = (ssidsByDevice[sB] || []).length;
          return (cA - cB) * dir;
        }
        case 'radios': {
          const cA = (radioByDevice[sA] || []).length;
          const cB = (radioByDevice[sB] || []).length;
          return (cA - cB) * dir;
        }
        default:
          return 0;
      }
    });

    const cols = [
      { id: 'device', label: 'Device' },
      { id: 'rrm', label: '5 GHz RRM plan' },
      { id: 'status', label: 'Status' },
      { id: 'group', label: 'Group assigned SSID' },
      { id: 'ssids', label: 'Assigned SSIDs' },
      { id: 'radios', label: 'Assigned Radio Profiles' },
    ];

    return (
      <Box>
        {rrmPlanIndex.hasData && rrmPlanIndex.excludedSerialSet.size > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{rrmPlanIndex.excludedSerialSet.size}</strong> NODE(s) are critical tree parents in the last Wi‑Fi RRM plan.
              You can still assign 5 GHz Public WiFi to them; the API will return a warning because it may affect mesh backhaul.
              For jnet2 CAP devices, pick <strong>5GHz_0</strong> or <strong>5GHz_1</strong> when assigning 5/6 GHz profiles.
            </Typography>
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Device Assignments</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" startIcon={<WifiIcon />}
              onClick={() => setAssignSSIDToDeviceDialog(true)}
              disabled={!canMutate || ssids.length === 0 || devicesForDirectWifiAssign.length === 0}>
              Assign SSID
            </Button>
            <Button size="small" variant="outlined" color="secondary" startIcon={<SettingsInputAntennaIcon />}
              onClick={() => setAssignRadioToDeviceDialog(true)}
              disabled={!canMutate || radioProfiles.length === 0 || devicesForDirectWifiAssign.length === 0}>
              Assign Radio
            </Button>
          </Stack>
        </Box>

        {deviceList.length === 0 ? (
          <Alert severity="info">No jnet2 nodes or CAP devices available.</Alert>
        ) : (
          <>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by serial or name…"
            value={devSearchQuery}
            onChange={(e) => {
              setDevSearchQuery(e.target.value);
              setDevPage(0);
            }}
            sx={{ mb: 2, maxWidth: 400 }}
            InputProps={{
              endAdornment: devSearchQuery ? (
                <IconButton size="small" aria-label="Clear search" onClick={() => { setDevSearchQuery(''); setDevPage(0); }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
          />
          {filteredDevices.length === 0 ? (
            <Alert severity="info">No devices match &quot;{devSearchQuery.trim()}&quot;.</Alert>
          ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {cols.map((c) => (
                    <TableCell key={c.id} sortDirection={devSortCol === c.id ? devSortDir : false}>
                      <TableSortLabel active={devSortCol === c.id} direction={devSortCol === c.id ? devSortDir : 'asc'}
                        onClick={() => handleSort(c.id)}>
                        <strong>{c.label}</strong>
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedDevices.slice(devPage * devRowsPerPage, devPage * devRowsPerPage + devRowsPerPage).map(dev => {
                  const serial = dev.serial_number?.toLowerCase() || '';
                  const devSSIDs = ssidsByDevice[serial] || [];
                  const devRadios = radioByDevice[serial] || [];
                  const grp = groupByDevice[serial] || null;
                  return (
                    <TableRow key={dev.serial_number} hover>
                      <TableCell>
                        <Box>
                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" fontWeight={500}>{dev.name || dev.serial_number}</Typography>
                            {isCapDevice(dev) && (
                              <Chip label="CAP" size="small" color="secondary" variant="outlined" sx={{ height: 20 }} />
                            )}
                          </Stack>
                          {dev.name && <Typography variant="caption" color="text.secondary">{dev.serial_number}</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell>{renderRrmPlanCell(dev)}</TableCell>
                      <TableCell>
                        <Chip
                          label={dev.connected ? 'Online' : 'Offline'}
                          size="small"
                          color={dev.connected ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 22 }}
                        />
                      </TableCell>
                      <TableCell>
                        {grp ? (
                          <Tooltip title={grp.ssids.length > 0 ? `Group SSID: ${grp.ssids.join(', ')}` : 'No SSID assigned to this group'}>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>{grp.groupName}</Typography>
                              {grp.ssids.length > 0 && (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                                  {grp.ssids.map((name) => (
                                    <Chip key={name} icon={<WifiIcon sx={{ fontSize: '0.8rem !important' }} />}
                                      label={name} size="small" color="info" variant="outlined" />
                                  ))}
                                </Stack>
                              )}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {devSSIDs.length === 0
                            ? <Typography variant="caption" color="text.secondary">None</Typography>
                            : devSSIDs.map(s => (
                                <Box key={s.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                  <Chip icon={<WifiIcon sx={{ fontSize: '0.8rem !important' }} />} label={s.ssid} size="small" color="primary" variant="outlined" />
                                  <Tooltip title="Unassign from device">
                                    <IconButton size="small" color="warning" sx={{ p: 0.25 }}
                                      onClick={() => handleUnassignSSIDFromDevice(s.id, dev.serial_number)}>
                                      <LinkOffIcon sx={{ fontSize: '0.85rem' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ))
                          }
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {devRadios.length === 0
                            ? <Typography variant="caption" color="text.secondary">None</Typography>
                            : devRadios.map(rp => (
                                <Box key={rp.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                  <Chip icon={<SettingsInputAntennaIcon sx={{ fontSize: '0.8rem !important' }} />}
                                    label={`${rp.name} (${formatRadioProfileBand(rp.band)})`} size="small" color="secondary" variant="outlined" />
                                  <Tooltip title="Unassign from device">
                                    <IconButton size="small" color="warning" sx={{ p: 0.25 }}
                                      onClick={() => handleUnassignRadioFromDevice(rp.id, dev.serial_number)}>
                                      <LinkOffIcon sx={{ fontSize: '0.85rem' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ))
                          }
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredDevices.length}
              page={devPage}
              onPageChange={(_, p) => setDevPage(p)}
              rowsPerPage={devRowsPerPage}
              onRowsPerPageChange={(e) => { setDevRowsPerPage(parseInt(e.target.value, 10)); setDevPage(0); }}
              rowsPerPageOptions={[20, 50, 100]}
            />
          </TableContainer>
          )}
          </>
        )}
      </Box>
    );
  };

  // ── render: Groups tab ────────────────────────────────────────────────────

  const renderGroupsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Device Groups</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<AssignmentIcon />}
            onClick={() => setAssignSSIDDialog(true)} disabled={!canMutate || groups.length === 0 || ssids.length === 0}>
            Assign SSID
          </Button>
          <Button size="small" variant="outlined" color="secondary" startIcon={<SettingsInputAntennaIcon />}
            onClick={() => setAssignRadioToGroupDialog(true)} disabled={!canMutate || groups.length === 0 || radioProfiles.length === 0}>
            Assign Radio
          </Button>
          <Button size="small" variant="outlined" startIcon={<AccountTreeIcon />}
            onClick={() => setSegmentToGroupDialogOpen(true)} disabled={!canMutate}>
            Segment → Group
          </Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />}
            onClick={() => setGroupDialog({ open: true, initial: null })} disabled={!canMutate}>
            New Group
          </Button>
        </Stack>
      </Box>

      {groupsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : groups.length === 0 ? (
        <Alert severity="info">
          No groups yet. Create a group or draw a polygon on the map to select devices.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <List disablePadding>
            {groups.map((group, idx) => {
              const isExpanded = expandedGroup === group.id;
              const isLast = idx === groups.length - 1;
              const hasSSIDs = (group.ssids || []).length > 0;
              const groupRadioProfiles = (group.radio_profiles || []);
              const groupRadioIds = group.radio_profile_ids || [];
              const hasRadio = groupRadioProfiles.length > 0 || groupRadioIds.length > 0;

              return (
                <Box key={group.id}>
                  <Box
                    onClick={() => setExpandedGroup(prev => prev === group.id ? null : group.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', px: 2, py: 1.5,
                      cursor: 'pointer', userSelect: 'none',
                      bgcolor: isExpanded ? 'action.selected' : 'background.paper',
                      borderLeft: isExpanded ? '3px solid' : '3px solid transparent',
                      borderLeftColor: isExpanded ? 'primary.main' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden' }}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap>{group.name}</Typography>
                      <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip icon={<DevicesIcon sx={{ fontSize: '0.85rem !important' }} />}
                          label={`${(group.devices || []).length} device${(group.devices || []).length !== 1 ? 's' : ''}`}
                          size="small" variant="outlined" />
                        {hasSSIDs ? (
                          <Tooltip title={`Group SSID: ${group.ssids.map((s) => s.ssid || s.name).join(', ')}`}>
                            <Chip
                              icon={<WifiIcon sx={{ fontSize: '0.85rem !important' }} />}
                              label={`Group SSID: ${group.ssids.map((s) => s.ssid || s.name).join(', ')}`}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ maxWidth: 280 }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip label="No group SSID" size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
                        )}
                        {hasRadio && (
                          <Chip icon={<SettingsInputAntennaIcon sx={{ fontSize: '0.85rem !important' }} />}
                            label={`${groupRadioProfiles.length || groupRadioIds.length} Radio`}
                            size="small" color="secondary" variant="outlined" />
                        )}
                      </Stack>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}
                      onClick={e => e.stopPropagation()}>
                      <Tooltip title="Actions">
                        <IconButton size="small"
                          onClick={e => setGroupMenu({ anchorEl: e.currentTarget, group })}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isExpanded ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                    </Box>
                  </Box>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2, py: 2, bgcolor: 'background.default' }}>

                      {/* Devices section */}
                      <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.68rem', letterSpacing: 1 }}>
                        Devices ({(group.devices || []).length}) — direct device SSID
                      </Typography>
                      {(group.devices || []).length === 0 ? (
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                          No devices in this group.
                        </Typography>
                      ) : (
                        <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                          {(group.devices || []).map((serial, di) => {
                            const dev = devices?.find(d => d.serial_number?.toLowerCase() === serial.toLowerCase());
                            const isLastDevice = di === group.devices.length - 1;
                            return (
                              <Box key={serial} sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75,
                                borderBottom: isLastDevice ? 'none' : '1px solid', borderBottomColor: 'divider', bgcolor: 'background.paper',
                              }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                  bgcolor: dev ? (dev.connected ? '#4caf50' : '#f44336') : '#bdbdbd' }} />
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={500} noWrap>{dev?.name || serial}</Typography>
                                  {dev?.name && <Typography variant="caption" color="text.secondary" noWrap>{serial}</Typography>}
                                </Box>
                                <Box sx={{ flexShrink: 0, maxWidth: '40%', display: 'flex', justifyContent: 'flex-end' }}>
                                  <DeviceDirectSsidIndicator serial={serial} ssidsByDevice={ssidsByDevice} compact />
                                </Box>
                                <Chip label={dev ? (dev.connected ? 'Online' : 'Offline') : 'Unknown'}
                                  size="small" color={dev?.connected ? 'success' : 'default'} variant="outlined"
                                  sx={{ fontSize: '0.68rem', height: 20, flexShrink: 0 }} />
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      {/* Group-level SSID profiles */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.68rem', letterSpacing: 1 }}>
                          Group SSID ({group.ssids?.length || 0})
                        </Typography>
                        {!hasSSIDs ? (
                          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                            No group-level SSID assigned.
                          </Typography>
                        ) : (
                          <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            {group.ssids.map((s, si) => {
                              const vlanProfile = vlans.find(v => v.id === s.vlan_profile_id);
                              return (
                                <Box key={s.id} sx={{
                                  display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75,
                                  borderBottom: si < group.ssids.length - 1 ? '1px solid' : 'none',
                                  borderBottomColor: 'divider', bgcolor: 'background.paper',
                                }}>
                                  <WifiIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={500} noWrap>{s.ssid || s.name}</Typography>
                                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.25, flexWrap: 'wrap', gap: 0.5 }}>
                                      {s.encryption && <Chip label={s.encryption} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />}
                                      {vlanProfile
                                        ? <Chip icon={<RouterIcon sx={{ fontSize: '0.75rem !important' }} />} label={`VLAN ${vlanProfile.name} #${vlanProfile.number}`} size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} />
                                        : s.vlan ? <Chip label={`VLAN ${s.vlan}`} size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} /> : null
                                      }
                                    </Stack>
                                  </Box>
                                  <Tooltip title="Remove from group">
                                    <IconButton size="small" color="warning"
                                      onClick={e => { e.stopPropagation(); handleUnassignSSID(s.id, group.id); }}>
                                      <LinkOffIcon sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>

                      {/* Radio profiles section */}
                      {hasRadio && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.68rem', letterSpacing: 1 }}>
                            Radio Profiles ({groupRadioProfiles.length || groupRadioIds.length})
                          </Typography>
                          <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            {(groupRadioProfiles.length > 0 ? groupRadioProfiles : groupRadioIds.map(id => radioProfiles.find(r => r.id === id)).filter(Boolean)).map((rp, ri, arr) => (
                              <Box key={rp.id} sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75,
                                borderBottom: ri < arr.length - 1 ? '1px solid' : 'none',
                                borderBottomColor: 'divider', bgcolor: 'background.paper',
                              }}>
                                <SettingsInputAntennaIcon fontSize="small" color="secondary" sx={{ flexShrink: 0 }} />
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={500} noWrap>{rp.name}</Typography>
                                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.25, flexWrap: 'wrap', gap: 0.5 }}>
                                    <Chip label={formatRadioProfileBand(rp.band)} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
                                    {rp.channel_width && <Chip label={rp.channel_width} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} />}
                                    {rp.channel && <Chip label={`Ch ${rp.channel}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} />}
                                    {rp.txpower && <Chip label={`${rp.txpower}dBm`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} />}
                                  </Stack>
                                </Box>
                                <Tooltip title="Remove from group">
                                  <IconButton size="small" color="warning"
                                    onClick={e => { e.stopPropagation(); handleUnassignRadioFromGroup(rp.id, group.id); }}>
                                    <LinkOffIcon sx={{ fontSize: '1rem' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {!hasSSIDs && !hasRadio && (
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 1.5, fontSize: '0.8rem' }}>
                          No profiles assigned. Use "Assign SSID" or "Assign Radio" above.
                        </Typography>
                      )}
                    </Box>
                  </Collapse>

                  {!isLast && <Divider />}
                </Box>
              );
            })}
          </List>
        </Paper>
      )}

      {/* Per-group actions menu */}
      <Menu
        anchorEl={groupMenu.anchorEl}
        open={Boolean(groupMenu.anchorEl)}
        onClose={() => setGroupMenu({ anchorEl: null, group: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled={!onHighlightGroupOnMap} onClick={() => {
          handleViewOnMap(groupMenu.group);
          setGroupMenu({ anchorEl: null, group: null });
        }}>
          <ListItemIcon><MapIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View on map</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          setGroupDialog({ open: true, initial: groupMenu.group });
          setGroupMenu({ anchorEl: null, group: null });
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem sx={{ color: 'error.main' }} onClick={() => {
          setDeleteConfirm({ open: true, type: 'group', item: groupMenu.group });
          setGroupMenu({ anchorEl: null, group: null });
        }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );

  // ── render: SSID Profiles tab ─────────────────────────────────────────────

  const renderSSIDsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">SSID Profiles</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<AssignmentIcon />}
            onClick={() => setAssignSSIDDialog(true)} disabled={!canMutate || groups.length === 0 || ssids.length === 0}>
            Assign to Group
          </Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />}
            onClick={() => setSSIDDialog({ open: true, initial: null })} disabled={!canMutate}>
            New SSID
          </Button>
        </Stack>
      </Box>

      {ssidsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : ssids.length === 0 ? (
        <Alert severity="info">No SSID profiles yet. Create one to assign to groups or devices.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>SSID</strong></TableCell>
                <TableCell><strong>Band</strong></TableCell>
                <TableCell><strong>Encryption</strong></TableCell>
                <TableCell><strong>VLAN Profile</strong></TableCell>
                <TableCell><strong>RADIUS Profile</strong></TableCell>
                <TableCell><strong>Groups</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ssids.map(ssid => {
                const assignedGroups = groups.filter(g => (g.ssid_ids || []).includes(ssid.id));
                const vlanProfile = vlans.find(v => v.id === ssid.vlan_profile_id);
                const radiusProfile = radiusProfiles.find(r => r.id === ssid.radius_profile_id);
                return (
                  <TableRow key={ssid.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WifiIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight={500}>{ssid.ssid}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ssid.band || DEFAULT_SSID_BAND}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell><Chip label={ssid.encryption} size="small" /></TableCell>
                    <TableCell>
                      {vlanProfile
                        ? <Chip icon={<RouterIcon sx={{ fontSize: '0.85rem !important' }} />}
                            label={`${vlanProfile.name} (#${vlanProfile.number})`} size="small" color="secondary" variant="outlined" />
                        : <Typography variant="caption" color="text.secondary">None</Typography>
                      }
                    </TableCell>
                    <TableCell>
                      {radiusProfile
                        ? <Chip icon={<SecurityIcon sx={{ fontSize: '0.85rem !important' }} />}
                            label={radiusProfile.name} size="small" color="warning" variant="outlined" />
                        : <Typography variant="caption" color="text.secondary">None</Typography>
                      }
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {assignedGroups.length === 0
                          ? <Typography variant="caption" color="text.secondary">None</Typography>
                          : assignedGroups.map(g => (
                              <Box key={g.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                <Chip label={g.name} size="small" />
                                <Tooltip title={`Remove from "${g.name}"`}>
                                  <IconButton size="small" color="warning" sx={{ p: 0.25 }}
                                    onClick={() => handleUnassignSSID(ssid.id, g.id)}>
                                    <LinkOffIcon sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ))
                        }
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Assign VLAN">
                          <span>
                            <IconButton size="small" color="secondary"
                              disabled={vlans.length === 0}
                              onClick={() => setAssignVLANToSSIDDialog({ open: true, ssid })}>
                              <RouterIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setSSIDDialog({ open: true, initial: ssid })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={assignedGroups.length > 0 ? `Assigned to ${assignedGroups.length} group(s) — unassign first` : 'Delete SSID profile'}>
                          <span>
                            <IconButton size="small" color="error"
                              disabled={assignedGroups.length > 0}
                              onClick={() => setDeleteConfirm({ open: true, type: 'ssid', item: ssid })}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // ── render: VLAN Profiles tab ─────────────────────────────────────────────

  const renderVLANsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">VLAN Profiles</Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />}
          onClick={() => setVLANDialog({ open: true, initial: null })} disabled={!canMutate}>
          New VLAN
        </Button>
      </Box>

      {vlansLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : vlans.length === 0 ? (
        <Alert severity="info">No VLAN profiles yet. Create one to assign to an SSID profile.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>VLAN Number</strong></TableCell>
                <TableCell><strong>Used by SSIDs</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vlans.map(vlan => {
                const usedBySSIDs = ssids.filter(s => s.vlan_profile_id === vlan.id);
                return (
                  <TableRow key={vlan.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RouterIcon fontSize="small" color="secondary" />
                        <Typography variant="body2" fontWeight={500}>{vlan.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Chip label={vlan.number} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {usedBySSIDs.length === 0
                          ? <Typography variant="caption" color="text.secondary">None</Typography>
                          : usedBySSIDs.map(s => <Chip key={s.id} icon={<WifiIcon sx={{ fontSize: '0.85rem !important' }} />} label={s.ssid} size="small" color="primary" variant="outlined" />)
                        }
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setVLANDialog({ open: true, initial: vlan })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={usedBySSIDs.length > 0 ? `Used by ${usedBySSIDs.length} SSID(s) — unassign first` : 'Delete VLAN profile'}>
                          <span>
                            <IconButton size="small" color="error"
                              disabled={usedBySSIDs.length > 0}
                              onClick={() => setDeleteConfirm({ open: true, type: 'vlan', item: vlan })}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // ── render: Radio Profiles tab ────────────────────────────────────────────

  const renderRadioProfilesTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Radio Profiles</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<AssignmentIcon />}
            onClick={() => setAssignRadioToGroupDialog(true)}
            disabled={groups.length === 0 || radioProfiles.length === 0}>
            Assign to Group
          </Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />}
            onClick={() => setRadioDialog({ open: true, initial: null })}>
            New Radio Profile
          </Button>
        </Stack>
      </Box>

      {radioProfilesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : radioProfiles.length === 0 ? (
        <Alert severity="info">No radio profiles yet. Create one to control channel, TX power, and bandwidth settings.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Band</strong></TableCell>
                <TableCell><strong>Channel</strong></TableCell>
                <TableCell><strong>Width</strong></TableCell>
                <TableCell><strong>TX Power</strong></TableCell>
                <TableCell><strong>Groups</strong></TableCell>
                <TableCell><strong>Devices</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {radioProfiles.map(rp => {
                const assignedGroups = groups.filter(g => (g.radio_profile_ids || []).includes(rp.id));
                const assignedDeviceCount = (rp.device_serials || []).length;
                return (
                  <TableRow key={rp.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SettingsInputAntennaIcon fontSize="small" color="secondary" />
                        <Typography variant="body2" fontWeight={500}>{rp.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatRadioProfileBand(rp.band)}
                        size="small"
                        color={(rp.band === 'all' || rp.band === '2.4GHz,6GHz') ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{rp.channel || 'Auto'}</TableCell>
                    <TableCell>{rp.channel_width || 'Default'}</TableCell>
                    <TableCell>{rp.txpower ? `${rp.txpower}dBm` : 'Auto'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {assignedGroups.length === 0
                          ? <Typography variant="caption" color="text.secondary">None</Typography>
                          : assignedGroups.map(g => (
                              <Box key={g.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                <Chip label={g.name} size="small" />
                                <Tooltip title={`Remove from "${g.name}"`}>
                                  <IconButton size="small" color="warning" sx={{ p: 0.25 }}
                                    onClick={() => handleUnassignRadioFromGroup(rp.id, g.id)}>
                                    <LinkOffIcon sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ))
                        }
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color={assignedDeviceCount > 0 ? 'text.primary' : 'text.secondary'}>
                        {assignedDeviceCount > 0 ? `${assignedDeviceCount} device${assignedDeviceCount !== 1 ? 's' : ''}` : 'None'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setRadioDialog({ open: true, initial: rp })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={
                          (assignedGroups.length > 0 || assignedDeviceCount > 0)
                            ? `Assigned to ${assignedGroups.length} group(s) and ${assignedDeviceCount} device(s) — unassign first`
                            : 'Delete radio profile'
                        }>
                          <span>
                            <IconButton size="small" color="error"
                              disabled={assignedGroups.length > 0 || assignedDeviceCount > 0}
                              onClick={() => setDeleteConfirm({ open: true, type: 'radio', item: rp })}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // ── render: RADIUS Profiles tab ───────────────────────────────────────────

  const renderRadiusProfilesTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">RADIUS Profiles</Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />}
          onClick={() => setRadiusDialog({ open: true, initial: null })}>
          New RADIUS Profile
        </Button>
      </Box>

      {radiusProfilesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : radiusProfiles.length === 0 ? (
        <Alert severity="info">No RADIUS profiles yet. Create one to enable 802.1X authentication on SSID profiles.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Auth Server</strong></TableCell>
                <TableCell><strong>Auth Port</strong></TableCell>
                <TableCell><strong>Acct Server</strong></TableCell>
                <TableCell><strong>Acct Port</strong></TableCell>
                <TableCell><strong>802.11w</strong></TableCell>
                <TableCell><strong>Used by SSIDs</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {radiusProfiles.map(rp => {
                const usedBySSIDs = ssids.filter(s => s.radius_profile_id === rp.id);
                return (
                  <TableRow key={rp.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon fontSize="small" color="warning" />
                        <Typography variant="body2" fontWeight={500}>{rp.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{rp.auth_server}</Typography></TableCell>
                    <TableCell>{rp.auth_port}</TableCell>
                    <TableCell>
                      {rp.acct_server
                        ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{rp.acct_server}</Typography>
                        : <Typography variant="caption" color="text.secondary">—</Typography>
                      }
                    </TableCell>
                    <TableCell>{rp.acct_port || '—'}</TableCell>
                    <TableCell>{rp.ieee80211w || '0'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {usedBySSIDs.length === 0
                          ? <Typography variant="caption" color="text.secondary">None</Typography>
                          : usedBySSIDs.map(s => (
                              <Box key={s.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                <Chip icon={<WifiIcon sx={{ fontSize: '0.85rem !important' }} />}
                                  label={s.ssid} size="small" color="primary" variant="outlined" />
                                <Tooltip title={`Remove from "${s.ssid}"`}>
                                  <IconButton size="small" color="warning" sx={{ p: 0.25 }}
                                    onClick={() => handleUnassignRadiusFromSSID(s.id)}>
                                    <LinkOffIcon sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ))
                        }
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setRadiusDialog({ open: true, initial: rp })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={usedBySSIDs.length > 0 ? `Used by ${usedBySSIDs.length} SSID(s) — unassign first` : 'Delete RADIUS profile'}>
                          <span>
                            <IconButton size="small" color="error"
                              disabled={usedBySSIDs.length > 0}
                              onClick={() => setDeleteConfirm({ open: true, type: 'radius', item: rp })}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // ── profiles sub-tab ──────────────────────────────────────────────────────

  const renderProfilesTab = () => (
    <Box>
      <Tabs
        value={profileTab}
        onChange={(_, v) => setProfileTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab icon={<WifiIcon />} iconPosition="start" label="SSID Profiles" />
        <Tab icon={<RouterIcon />} iconPosition="start" label="VLAN Profiles" />
        <Tab icon={<SettingsInputAntennaIcon />} iconPosition="start" label="Radio Profiles" />
        <Tab icon={<SecurityIcon />} iconPosition="start" label="RADIUS Profiles" />
      </Tabs>
      <TabPanel value={profileTab} index={0}>{renderSSIDsTab()}</TabPanel>
      <TabPanel value={profileTab} index={1}>{renderVLANsTab()}</TabPanel>
      <TabPanel value={profileTab} index={2}>{renderRadioProfilesTab()}</TabPanel>
      <TabPanel value={profileTab} index={3}>{renderRadiusProfilesTab()}</TabPanel>
    </Box>
  );

  // ── syslog tab ────────────────────────────────────────────────────────────

  // Sentinel value used as the "All Devices" option in the device multi-select
  const SYSLOG_ALL_DEVICES = useMemo(() => ({ serial_number: '__ALL__', name: 'All Devices' }), []);

  const syslogDeviceOptions = useMemo(
    () => [SYSLOG_ALL_DEVICES, ...(devices || [])],
    [SYSLOG_ALL_DEVICES, devices],
  );

  // Whether the selection currently means "apply to everyone"
  const syslogIsAllDevices = syslogScope === 'device' &&
    syslogSelectedDevices.some((d) => d.serial_number === '__ALL__');

  const syslogTargetDevices = useMemo(() => {
    const all = devices || [];
    if (syslogScope === 'device') {
      if (syslogSelectedDevices.some((d) => d.serial_number === '__ALL__')) return all;
      return syslogSelectedDevices.filter((d) => d.serial_number !== '__ALL__');
    }
    if (syslogScope === 'group' && syslogSelectedGroups.length > 0) {
      const serials = new Set(
        syslogSelectedGroups.flatMap((g) => (g.devices || []).map((s) => s.toLowerCase())),
      );
      return all.filter((d) => serials.has((d.serial_number || '').toLowerCase()));
    }
    return [];
  }, [devices, syslogScope, syslogSelectedDevices, syslogSelectedGroups]);


  const handleSyslogApply = useCallback(async () => {
    if (!syslogCfg.ip.trim()) {
      setSyslogStatus({ type: 'warning', message: 'Syslog server IP address is required' });
      return;
    }
    if (syslogTargetDevices.length === 0) {
      setSyslogStatus({ type: 'warning', message: 'No devices in the selected scope' });
      return;
    }
    setSyslogSaving(true);
    setSyslogStatus(null);
    try {
      const serials = syslogTargetDevices.map((dev) => dev.serial_number);
      const response = await api.applySyslogConfigBatch(serials, syslogCfg);
      const results = api.mapBatchOperationResults(response);
      const ok = results.filter((r) => r.success).length;
      const fail = results.filter((r) => !r.success).length;
      if (fail === 0) {
        setSyslogStatus({ type: 'success', message: `Syslog config applied to ${ok} device(s)` });
      } else {
        setSyslogStatus({ type: 'warning', message: `Applied to ${ok} device(s); ${fail} failed (device may not support syslog)` });
      }
    } catch (err) {
      setSyslogStatus({ type: 'error', message: err?.message || 'Failed to apply syslog config' });
    } finally {
      setSyslogSaving(false);
    }
  }, [syslogCfg, syslogTargetDevices]);

  const handleSyslogClearTargets = useCallback(async () => {
    if (syslogTargetDevices.length === 0) {
      setSyslogStatus({ type: 'warning', message: 'No devices in the selected scope' });
      return;
    }
    setSyslogClearing(true);
    setSyslogStatus(null);
    try {
      const serials = syslogTargetDevices.map((dev) => dev.serial_number);
      const response = await api.applySyslogConfigBatch(serials, { ...SYSLOG_EMPTY_CONFIG }, { clear: true });
      const results = api.mapBatchOperationResults(response);
      const ok = results.filter((r) => r.success).length;
      const fail = results.filter((r) => !r.success).length;
      if (fail === 0) {
        setSyslogStatus({ type: 'success', message: `Syslog config cleared on ${ok} device(s)` });
      } else {
        setSyslogStatus({ type: 'warning', message: `Cleared on ${ok} device(s); ${fail} failed` });
      }
    } catch (err) {
      setSyslogStatus({ type: 'error', message: err?.message || 'Failed to clear syslog config' });
    } finally {
      setSyslogClearing(false);
    }
  }, [syslogTargetDevices]);

  const renderSyslogTab = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Syslog Server Configuration</Typography>

      {/* Scope selector */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>Apply to:</Typography>
        <ToggleButtonGroup
          value={syslogScope}
          exclusive
          onChange={(_, v) => {
            if (!v) return;
            setSyslogScope(v);
            setSyslogSelectedGroups([]);
            setSyslogSelectedDevices([]);
            setSyslogStatus(null);
          }}
          size="small"
        >
          <ToggleButton value="device">By Device</ToggleButton>
          <ToggleButton value="group">By Group</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Device multi-select (first option = All Devices) */}
      {syslogScope === 'device' && (
        <Box sx={{ mb: 3 }}>
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 420 }}
            options={syslogDeviceOptions}
            getOptionLabel={(d) =>
              d.serial_number === '__ALL__'
                ? 'All Devices'
                : d.name && d.name !== d.serial_number
                  ? `${d.name} (${d.serial_number})`
                  : d.serial_number
            }
            isOptionEqualToValue={(o, v) => o.serial_number === v.serial_number}
            value={syslogSelectedDevices}
            onChange={(_, newVal) => {
              // If "All Devices" is being added, keep only the sentinel
              const hasAll = newVal.some((d) => d.serial_number === '__ALL__');
              setSyslogSelectedDevices(hasAll ? [SYSLOG_ALL_DEVICES] : newVal.filter((d) => d.serial_number !== '__ALL__'));
              setSyslogStatus(null);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select Devices" placeholder={syslogSelectedDevices.length === 0 ? 'Choose devices…' : ''} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((d, index) => (
                <Chip
                  key={d.serial_number}
                  label={d.serial_number === '__ALL__' ? 'All Devices' : (d.name || d.serial_number)}
                  size="small"
                  color={d.serial_number === '__ALL__' ? 'primary' : 'default'}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        </Box>
      )}

      {/* Group multi-select */}
      {syslogScope === 'group' && (
        <Autocomplete
          multiple
          size="small"
          sx={{ mb: 3, maxWidth: 560 }}
          options={groups}
          getOptionLabel={(g) => `${g.name} (${(g.devices || []).length} device${(g.devices || []).length !== 1 ? 's' : ''})`}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={syslogSelectedGroups}
          onChange={(_, newVal) => {
            setSyslogSelectedGroups(newVal);
            setSyslogStatus(null);
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Groups" placeholder={syslogSelectedGroups.length === 0 ? 'Choose groups…' : ''} />
          )}
          renderTags={(value, getTagProps) =>
            value.map((g, index) => (
              <Chip key={g.id} label={g.name} size="small" {...getTagProps({ index })} />
            ))
          }
        />
      )}

      {/* Target summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {syslogScope === 'device' && syslogSelectedDevices.length === 0 && 'Select one or more devices above, or choose "All Devices"'}
        {syslogScope === 'device' && syslogIsAllDevices && `Target: all ${(devices || []).length} device(s) (apply or clear)`}
        {syslogScope === 'device' && !syslogIsAllDevices && syslogTargetDevices.length > 0 &&
          `Target: ${syslogTargetDevices.length} selected device(s) (apply or clear)`}
        {syslogScope === 'group' && syslogSelectedGroups.length === 0 && 'Select one or more groups above'}
        {syslogScope === 'group' && syslogSelectedGroups.length > 0 &&
          `Target: ${syslogTargetDevices.length} device(s) across ${syslogSelectedGroups.length} group(s) (apply or clear)`}
      </Typography>

      {/* Status alert */}
      {syslogStatus && (
        <Alert severity={syslogStatus.type} sx={{ mb: 2 }} onClose={() => setSyslogStatus(null)}>
          {syslogStatus.message}
        </Alert>
      )}

      {/* Syslog form */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, maxWidth: 560 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          Syslog Server
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="IP Address"
              value={syslogCfg.ip}
              onChange={(e) => setSyslogCfg((prev) => ({ ...prev, ip: e.target.value }))}
              size="small"
              fullWidth
              placeholder="e.g. 192.168.1.100"
            />
            <TextField
              label="Port"
              value={syslogCfg.port}
              onChange={(e) => setSyslogCfg((prev) => ({ ...prev, port: e.target.value }))}
              size="small"
              sx={{ minWidth: 120 }}
              placeholder="e.g. 514"
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Protocol</InputLabel>
              <Select
                value={syslogCfg.proto || 'udp'}
                label="Protocol"
                onChange={(e) => setSyslogCfg((prev) => ({ ...prev, proto: e.target.value }))}
              >
                <MenuItem value="udp">UDP</MenuItem>
                <MenuItem value="tcp">TCP</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Remote Logging</InputLabel>
              <Select
                value={syslogCfg.log_remote || ''}
                label="Remote Logging"
                onChange={(e) => setSyslogCfg((prev) => ({ ...prev, log_remote: e.target.value }))}
              >
                <MenuItem value="">Off</MenuItem>
                <MenuItem value="1">On</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
        <Button
          variant="contained"
          startIcon={syslogSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSyslogApply}
          disabled={syslogSaving || syslogClearing || syslogTargetDevices.length === 0}
        >
          {syslogSaving
            ? 'Applying…'
            : `Apply to ${syslogTargetDevices.length} device${syslogTargetDevices.length !== 1 ? 's' : ''}`}
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={syslogClearing ? <CircularProgress size={16} color="inherit" /> : <ClearIcon />}
          onClick={handleSyslogClearTargets}
          disabled={syslogSaving || syslogClearing || syslogTargetDevices.length === 0}
        >
          {syslogClearing
            ? 'Clearing…'
            : `Clear syslog on ${syslogTargetDevices.length} device${syslogTargetDevices.length !== 1 ? 's' : ''}`}
        </Button>
      </Stack>

    </Box>
  );

  // ── main return ───────────────────────────────────────────────────────────

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Configuration</Typography>
      {!canMutate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only mode — Public WiFi configuration changes are disabled.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable" scrollButtons="auto">
        <Tab icon={<DevicesIcon />} iconPosition="start" label="Devices" />
        <Tab icon={<GroupsIcon />} iconPosition="start" label="Groups" />
        <Tab icon={<FolderIcon />} iconPosition="start" label="Profiles" />
        <Tab icon={<DnsIcon />} iconPosition="start" label="Syslog" />
      </Tabs>

      <TabPanel value={tab} index={0}>{renderDevicesTab()}</TabPanel>
      <TabPanel value={tab} index={1}>{renderGroupsTab()}</TabPanel>
      <TabPanel value={tab} index={2}>{renderProfilesTab()}</TabPanel>
      <TabPanel value={tab} index={3}>{renderSyslogTab()}</TabPanel>

      {/* Dialogs */}
      <GroupDialog
        open={groupDialog.open}
        onClose={() => setGroupDialog({ open: false, initial: null })}
        onSave={handleSaveGroup}
        initial={groupDialog.initial}
        devices={devices}
        ssidsByDevice={ssidsByDevice}
        ssids={ssids}
      />
      <SegmentToGroupDialog
        open={segmentToGroupDialogOpen}
        onClose={() => setSegmentToGroupDialogOpen(false)}
        onCreated={fetchGroups}
      />
      <SSIDDialog
        open={ssidDialog.open}
        onClose={() => setSSIDDialog({ open: false, initial: null })}
        onSave={handleSaveSSID}
        initial={ssidDialog.initial}
        vlans={vlans}
        radiusProfiles={radiusProfiles}
        groups={groups}
      />
      <VLANDialog
        open={vlanDialog.open}
        onClose={() => setVLANDialog({ open: false, initial: null })}
        onSave={handleSaveVLAN}
        initial={vlanDialog.initial}
      />
      <RadioProfileDialog
        open={radioDialog.open}
        onClose={() => setRadioDialog({ open: false, initial: null })}
        onSave={handleSaveRadioProfile}
        initial={radioDialog.initial}
      />
      <RADIUSDialog
        open={radiusDialog.open}
        onClose={() => setRadiusDialog({ open: false, initial: null })}
        onSave={handleSaveRadiusProfile}
        initial={radiusDialog.initial}
      />
      <AssignSSIDToGroupsDialog
        open={assignSSIDDialog}
        onClose={() => setAssignSSIDDialog(false)}
        onAssign={handleAssignSSIDToGroups}
        items={ssids}
        groups={groups}
        vlans={vlans}
        devices={devices}
      />
      <AssignRadioToGroupsDialog
        open={assignRadioToGroupDialog}
        onClose={() => setAssignRadioToGroupDialog(false)}
        onAssign={handleAssignRadioToGroups}
        items={radioProfiles}
        groups={groups}
      />
      <AssignProfileToDevicesDialog
        open={assignSSIDToDeviceDialog}
        onClose={() => setAssignSSIDToDeviceDialog(false)}
        onAssign={handleAssignSSIDToDevices}
        title="Assign SSID Profile to Devices"
        items={ssids}
        itemLabel="SSID Profile"
        devices={devicesForDirectWifiAssign}
        profileKind="ssid"
        vlans={vlans}
      />
      <AssignProfileToDevicesDialog
        open={assignRadioToDeviceDialog}
        onClose={() => setAssignRadioToDeviceDialog(false)}
        onAssign={handleAssignRadioToDevices}
        title="Assign Radio Profile to Devices"
        items={radioProfiles}
        itemLabel="Radio Profile"
        devices={devicesForDirectWifiAssign}
        profileKind="radio"
      />
      <AssignVLANToSSIDDialog
        open={assignVLANToSSIDDialog.open}
        ssid={assignVLANToSSIDDialog.ssid}
        vlans={vlans}
        onClose={() => setAssignVLANToSSIDDialog({ open: false, ssid: null })}
        onAssign={handleAssignVLANToSSID}
      />

      {/* Confirm delete dialog */}
      <Dialog open={deleteConfirm.open} onClose={() => !deleting && setDeleteConfirm({ open: false, type: null, item: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {deleteConfirm.type === 'group' && (
            <Typography>
              Delete group <strong>{deleteConfirm.item?.name}</strong>?
              This will also remove the group reference from any assigned profiles.
            </Typography>
          )}
          {deleteConfirm.type === 'ssid' && (
            <Typography>
              Delete SSID profile <strong>{deleteConfirm.item?.ssid}</strong>? This action cannot be undone.
            </Typography>
          )}
          {deleteConfirm.type === 'vlan' && (
            <Typography>
              Delete VLAN profile <strong>{deleteConfirm.item?.name}</strong>? This action cannot be undone.
            </Typography>
          )}
          {deleteConfirm.type === 'radio' && (
            <Typography>
              Delete radio profile <strong>{deleteConfirm.item?.name}</strong>? This action cannot be undone.
            </Typography>
          )}
          {deleteConfirm.type === 'radius' && (
            <Typography>
              Delete RADIUS profile <strong>{deleteConfirm.item?.name}</strong>? This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, type: null, item: null })} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirmed} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
